// @ts-nocheck
// =================================================================
// PAGE 1 - BOOKING DETAILS & FLEET MANAGEMENT
// PART 1/2: IMPORTS, STATES, FUNCTIONS
// =================================================================

import { useState, useMemo, useRef, useEffect, useContext } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { DataContext } from './App';
import { generateLuxuryPDF } from './utils/LuxuryPDFGenerator';
import { sendCheckInEmail } from './services/emailService';
import authService from './authService';
import { getVessels, getBookings, getBooking, updateBooking, getPage1DataHybrid, savePage1DataHybrid, Page1FormData, checkDuplicateCharterCode, checkDateOverlap, searchBookingByCode, loadChartererData, getPage5Agreement } from './services/apiService';
import { saveBookingSync, getBookingSync, syncToPage1Format, page1ToSyncFormat } from './utils/bookingSyncUtils';
import { extractCode } from './utils/searchUtils';
import { getCharterWindow, athensTodayYmd } from './utils/charterWindow';
import { orderEarlyCheckout, ecRowBg, ecMatches } from './utils/earlyCheckout';
import { syncCheckinPreview } from './utils/previewMode';

// 🔥 SIGNATURE COMPRESSION FUNCTION
const compressSignature = (base64Image) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const maxWidth = 400;
      const maxHeight = 200;
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      const compressed = canvas.toDataURL('image/png', 0.7);
      resolve(compressed);
    };
    img.src = base64Image;
  });
};

const I18N = {
  en: {
    title: "CHECK-IN / CHECK-OUT REPORT",
    bookingNumber: "1. BOOKING NUMBER",
    category: "Category",
    vessel: "Vessel",
    dateIn: "2. Date of Check-in",
    timeIn: "Time of Check-in",
    dateOut: "3. Date of Check-out",
    timeOut: "Time of Check-out",
    firstName: "4. Skipper's First Name",
    lastName: "Skipper's Last Name",
    address: "5. Skipper's Address",
    email: "6. Skipper's Email Address",
    phone: "7. Skipper's Mobile Number",
    selectCategory: "-- Select Category --",
    selectVessel: "-- Select Vessel --",
    selectCatFirst: "Select category first",
    cat: "Catamaran",
    mono: "Monohull",
    selected: "Selected:",
    recentEntries: "Recent entries:",
    back: "Back",
    saveDraft: "Save draft",
    clear: "Clear",
    pdf: "PDF",
    next: "Next",
    checkin: "Check-in",
    checkout: "Check-out",
    mode: "Mode:",
    clearAllData: "Clear All Data",
    confirmClearAll: "⚠️ WARNING: This will delete ALL check-in/check-out data from localStorage. Are you sure?",
    allDataCleared: "All data cleared successfully!",
    selectBooking: "Select Booking:",
    createNewBooking: "+ Create New Booking",
    activeBooking: "Active Booking:",
    recentBookings: "Recent Bookings:",
    noBookings: "No bookings yet",
    bookingInfo: "Booking Information",
    notSet: "[Not set]",
    notSelected: "[Not selected]",
    status: "Status:",
    synced: "Synced",
    offline: "Offline",
    syncing: "Syncing...",
    todayBookings: "Today's Bookings",
    vesselsToday: "vessels today",
    showAllBookings: "Show All Bookings",
    totalBookings: "total",
  },
  el: {
    title: "ΑΝΑΦΟΡΑ ΕΠΙΒΙΒΑΣΗΣ / ΑΠΟΒΙΒΑΣΗΣ",
    bookingNumber: "1. ΑΡΙΘΜΟΣ ΚΡΑΤΗΣΗΣ",
    category: "Κατηγορία",
    vessel: "Σκάφος",
    dateIn: "2. Ημερομηνία Επιβίβασης",
    timeIn: "Ώρα Επιβίβασης",
    dateOut: "3. Ημερομηνία Αποβίβασης",
    timeOut: "Ώρα Αποβίβασης",
    firstName: "4. Όνομα Κυβερνήτη",
    lastName: "Επώνυμο Κυβερνήτη",
    address: "5. Διεύθυνση Κυβερνήτη",
    email: "6. Email Κυβερνήτη",
    phone: "7. Κινητό Κυβερνήτη",
    selectCategory: "-- Επιλέξτε Κατηγορία --",
    selectVessel: "-- Επιλέξτε Σκάφος --",
    selectCatFirst: "Πρώτα επιλέξτε κατηγορία",
    cat: "Καταμαράν",
    mono: "Μονόχαλ",
    selected: "Επιλογή:",
    recentEntries: "Πρόσφατες καταχωρήσεις:",
    back: "Πίσω",
    saveDraft: "Αποθήκευση",
    clear: "Καθαρισμός",
    pdf: "PDF",
    next: "Επόμενο",
    checkin: "Επιβίβαση",
    checkout: "Αποβίβαση",
    mode: "Λειτουργία:",
    clearAllData: "Διαγραφή Όλων των Δεδομένων",
    confirmClearAll: "⚠️ ΠΡΟΣΟΧΗ: Θα διαγραφούν ΟΛΑ τα δεδομένα check-in/check-out από το localStorage. Είστε σίγουροι;",
    allDataCleared: "Όλα τα δεδομένα διαγράφηκαν επιτυχώς!",
    selectBooking: "Επιλογή Κράτησης:",
    createNewBooking: "+ Νέα Κράτηση",
    activeBooking: "Ενεργή Κράτηση:",
    recentBookings: "Πρόσφατες Κρατήσεις:",
    noBookings: "Δεν υπάρχουν κρατήσεις",
    bookingInfo: "Πληροφορίες Κράτησης",
    notSet: "[Δεν έχει οριστεί]",
    notSelected: "[Δεν έχει επιλεγεί]",
    status: "Κατάσταση:",
    synced: "Συγχρονισμένο",
    offline: "Εκτός σύνδεσης",
    syncing: "Συγχρονίζεται...",
    todayBookings: "Σημερινές Κρατήσεις",
    vesselsToday: "σκάφη σήμερα",
    showAllBookings: "Όλες οι Κρατήσεις",
    totalBookings: "σύνολο",
  },
};

// 🔥 SHARED FLEET SERVICE
const FLEET_STORAGE_KEY = 'app_fleet_vessels';

const FleetService = {
  async initialize() {
    try {
      const vessels = await getVessels();
      localStorage.setItem(FLEET_STORAGE_KEY, JSON.stringify(vessels));
      console.log('✅ Fleet initialized from API:', vessels.length, 'boats');
    } catch (error) {
      console.error('Error loading fleet from API:', error);
    }
  },

  getBoatsByCategory() {
    const boats = JSON.parse(localStorage.getItem(FLEET_STORAGE_KEY) || '[]');
    const grouped = {};
    boats.forEach(boat => {
      if (!grouped[boat.type]) grouped[boat.type] = [];
      grouped[boat.type].push(boat.name);
    });
    return grouped;
  }
};

FleetService.initialize();
const COUNTRY_CODES = [
  { code: "+30", country: "GR", flag: "🇬🇷" },
  { code: "+44", country: "GB", flag: "🇬🇧" },
  { code: "+1", country: "US", flag: "🇺🇸" },
  { code: "+49", country: "DE", flag: "🇩🇪" },
  { code: "+33", country: "FR", flag: "🇫🇷" },
  { code: "+39", country: "IT", flag: "🇮🇹" },
  { code: "+34", country: "ES", flag: "🇪🇸" },
  { code: "+31", country: "NL", flag: "🇳🇱" },
];

const stripNumber = (s) => String(s || '').replace(/^\s*\d+\.?\s*/, "");
const isFilled = (v) => (typeof v === 'string' ? v.trim().length > 0 : !!v);

// 🔥 FORMAT DATE: YYYY-MM-DD → DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  
  // If already in DD/MM/YYYY format, return as is
  if (dateStr.includes('/')) return dateStr;
  
  // Convert YYYY-MM-DD → DD/MM/YYYY
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  
  return dateStr;
};

const saveBookingData = async (bookingNumber: string, data: any) => {
  if (!bookingNumber) return;

  try {
    // ✅ Use Page 1 specific API - saves to /api/page1.php AND localStorage
    // 🔥 Always mark as source: 'page1' so Fleet Management can identify it
    const result = await savePage1DataHybrid(bookingNumber, {
      bookingNumber,
      vesselName: data.vesselName,
      vesselId: data.vesselId,
      vesselCategory: data.vesselCategory,
      checkInDate: data.checkInDate,
      checkInTime: data.checkInTime,
      checkOutDate: data.checkOutDate,
      checkOutTime: data.checkOutTime,
      skipperFirstName: data.skipperFirstName,
      skipperLastName: data.skipperLastName,
      skipperAddress: data.skipperAddress,
      skipperEmail: data.skipperEmail,
      skipperPhone: data.skipperPhone,
      phoneCountryCode: data.phoneCountryCode,
      mode: data.mode,
      status: data.status || 'Draft',
      source: 'page1'  // 🔥 Mark as coming from Page 1 check-in
    } as Page1FormData);

    console.log('📝 PAGE 1: Saving with source=page1, status=', data.status || 'Draft');

    // FIX: removed localStorage.setItem - multi-user architecture, no per-device state
    console.log('💾 Saved booking to API:', bookingNumber, result.synced ? '(synced)' : '(failed)');
  } catch (error) {
    console.error('❌ Error saving booking to API:', error);
  }
};

const loadBookingData = async (bookingNumber: string) => {
  if (!bookingNumber) return null;
  try {
    const data = await getPage1DataHybrid(bookingNumber);
    if (data) return data;

    const booking = await getBooking(bookingNumber);
    if (booking?.bookingData) return booking.bookingData;

    const code = extractCode(bookingNumber);
    if (code && code !== bookingNumber) {
      const fuzzy = await searchBookingByCode(code);
      if (fuzzy?.booking_data) return fuzzy.booking_data;
    }

    return null;
  } catch (error) {
    return null;
  }
};

// Get all bookings from API (returns empty for sync calls - use async version)
const getAllBookingsSync = (): any[] => {
  // No localStorage - must use async API calls
  console.warn('⚠️ getAllBookingsSync called - use async version instead');
  return [];
};

// Get all bookings from API
const getAllBookingsFromAPI = async () => {
  try {
    const bookings = await getBookings();
    if (!bookings || typeof bookings !== 'object') return [];

    return Object.keys(bookings).map(bookingNumber => ({
      bookingNumber,
      ...(bookings[bookingNumber]?.bookingData || {}),
      lastModified: bookings[bookingNumber]?.lastModified
    }));
  } catch (error) {
    console.error('Error getting all bookings from API:', error);
    return [];
  }
};

// Get today's check-outs (returns empty for sync - use context data)
const getTodayCheckoutsSync = (): any[] => {
  console.warn('⚠️ getTodayCheckoutsSync called - use context data instead');
  return [];
};

const getTodayCheckouts = getTodayCheckoutsSync;
// 🔥 AUTO-MODE: compute mandatory mode from booking dates (TZ-safe, local midnight)
// Returns 'out' if today is in check-out window (endDate-1 OR endDate) — HARD LOCK.
// Returns 'in' if today === checkInDate. Otherwise null (out of window, caller picks default).
const computeAutoMode = (ciStr: any, coStr: any): 'in' | 'out' | null => {
  // 🔥 CHARTER WINDOW (v6.196): delegate to single source of truth (Europe/Athens,
  // monohimeros-aware). Check-in day always wins; else check-out window (last 2 days).
  const w = getCharterWindow(ciStr, coStr);
  if (w.isCheckInDay) return 'in';
  if (w.isCheckOutWindow) return 'out';
  return null;
};

export default function Page1() {
  const [lang, setLang] = useState("en");
  const t = I18N[lang];
  const navigate = useNavigate();

  // FIX: Midnight rollover - re-render when day changes
  const [, setTodayTick] = useState(0);
  useEffect(() => {
    const computeYmd = () => {
      const d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    };
    let lastYmd = computeYmd();
    const interval = setInterval(() => {
      const currentYmd = computeYmd();
      if (currentYmd !== lastYmd) {
        lastYmd = currentYmd;
        setTodayTick(t => t + 1);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  const location = useLocation();
  
  const context = useContext(DataContext);
  const { data: contextData, updateData, globalBookings, isRefreshing: globalIsRefreshing, refreshBookings } = context || {};
  
  const [currentBookingNumber, setCurrentBookingNumber] = useState(() => contextData?.bookingNumber || ''); // 🔥 BUG A FIX: read from context on mount (persists across back navigation)
  
  const [mode, setMode] = useState(() => {
    // 🔥 FIX: Read from DataContext first (persists across navigation), then saved data, then default
    if (contextData?.mode) return contextData.mode;
    if (currentBookingNumber) {
      const data = loadBookingData(currentBookingNumber);
      return data?.mode || 'in';
    }
    return 'in';
  });

  // 🔥 FIX: Listen to modeChanged event so Page 1 syncs when mode changes elsewhere
  useEffect(() => {
    const handleModeChange = (e: any) => {
      if (e?.detail?.mode) {
        console.log('📢 Page1 received modeChanged event:', e.detail.mode);
        setMode(e.detail.mode);
      }
    };
    window.addEventListener('modeChanged', handleModeChange);
    return () => window.removeEventListener('modeChanged', handleModeChange);
  }, []);

  // 🔥 FIX: On mount, if contextData has a mode, sync local state (covers Back navigation case)
  useEffect(() => {
    if (contextData?.mode && contextData.mode !== mode) {
      console.log('🔄 Page1 syncing mode from context:', contextData.mode);
      setMode(contextData.mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextData?.mode]);

  // 🔥 BUG A FIX: On context.bookingNumber change, sync local currentBookingNumber (covers Back navigation case)
  useEffect(() => {
    if (contextData?.bookingNumber && contextData.bookingNumber !== currentBookingNumber) {
      console.log('🔄 Page1 syncing bookingNumber from context:', contextData.bookingNumber);
      setCurrentBookingNumber(contextData.bookingNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextData?.bookingNumber]);
  
  const [networkStatus, setNetworkStatus] = useState(
    navigator.onLine ? 'online' : 'offline'
  );
  
  const [showBookingSelector, setShowBookingSelector] = useState(false);
  
  // 🔥 NEW: Filter state for booking selector
  const [showAllBookings, setShowAllBookings] = useState(false);
  
  // 🔥 NEW: Duplicate booking warning
  const [duplicateBooking, setDuplicateBooking] = useState(null);
  const bookingInputRef = useRef(null);
  
  const speakLabel = (text) => {
    console.log('Label:', text);
  };

  const speak = (text) => {
    console.log('Speaking:', text);
  };
  
  const [isEmployee, setIsEmployee] = useState(false);
  const [authResolved, setAuthResolved] = useState(false); // 🔒 true αφού διαβαστεί το auth στο mount
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [employeeCode, setEmployeeCode] = useState("");
  const [showEmployeeCode, setShowEmployeeCode] = useState(false);

  // EMPLOYEE_CODES removed - now using authService

  const [currentEmployee, setCurrentEmployee] = useState(null);

  // 🔥 Read logged-in user (from Gate Page) on mount so role-gated sections appear without re-login
  useEffect(() => {
    let u: any = null;
    try { u = JSON.parse(sessionStorage.getItem('auth_current_user') || 'null'); } catch (e) { u = null; }
    if (!u) u = authService.getCurrentUser();
    if (u) {
      setIsEmployee(true);
      setCurrentEmployee({ ...(u.permissions || {}), role: u.role });
    }
    setAuthResolved(true); // 🔒 auth «λύθηκε» — ξέρουμε σίγουρα staff vs customer (ΠΑΝΤΑ τρέχει)
  }, []);

  // Refresh counter for re-fetching from API
  const [refreshCounter, setRefreshCounter] = useState(0);

  const bookingRef = useRef(null);
  const vesselRef = useRef(null);
  const checkInRef = useRef(null);
  const checkOutRef = useRef(null);
  const nameRef = useRef(null);
  const addressRef = useRef(null);
  const contactRef = useRef(null);

  const [form, setForm] = useState({
    bookingNumber: "",
    vesselCategory: "",
    vesselName: "",
    vesselId: "",
    checkInDate: "",
    checkInTime: "",
    checkOutDate: "",
    checkOutTime: "",
    // Charterer fields
    chartererFirstName: "",
    chartererIsCompany: false, chartererCompanyName: "", chartererCountry: "",
    chartererLastName: "",
    chartererAddress: "",
    chartererEmail: "",
    chartererPhone: "",
    chartererDob: "",
    chartererGender: "",
    chartererNationality: "",
    sameAsSkipper: false,
    // Skipper fields
    skipperFirstName: "",
    skipperLastName: "",
    skipperAddress: "",
    skipperEmail: "",
    skipperPhone: "",
    skipperDob: "",
    skipperGender: "",
    skipperNationality: "",
    phoneCountryCode: "+30",
    status: "Draft",  // 🔥 Page 1 bookings always start as Draft
  });

  // 🔥 NEW: Track unsaved changes to prevent data loss
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedForm, setLastSavedForm] = useState<string>('');

  // 🔥 Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Έχετε μη αποθηκευμένες αλλαγές. Είστε σίγουροι ότι θέλετε να φύγετε;';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // 🔥 Track form changes to detect unsaved changes
  useEffect(() => {
    const currentFormString = JSON.stringify(form);
    if (lastSavedForm && currentFormString !== lastSavedForm) {
      setHasUnsavedChanges(true);
    }
  }, [form, lastSavedForm]);

  // 🔥 AUTO-SAVE: Save form automatically after 3 seconds of no changes
  useEffect(() => {
    // Only auto-save if we have a booking number and vessel selected
    if (!form.bookingNumber || !form.vesselId) return;

    const autoSaveTimer = setTimeout(() => {
      if (hasUnsavedChanges) {
        console.log('💾 AUTO-SAVE triggered for booking:', form.bookingNumber);
        const dataToSave = { ...form, mode };
        saveBookingData(form.bookingNumber, dataToSave);
        setLastSavedForm(JSON.stringify(form));
        setHasUnsavedChanges(false);
      }
    }, 3000); // Auto-save after 3 seconds of no changes

    return () => clearTimeout(autoSaveTimer);
  }, [form, hasUnsavedChanges, mode]);

  const [history, setHistory] = useState({
    bookingNumbers: [],
    firstNames: [],
    lastNames: [],
    addresses: [],
    emails: [],
    phones: [],
  });

  const [showSuggestions, setShowSuggestions] = useState({
    bookingNumber: false,
    firstName: false,
    lastName: false,
    address: false,
    email: false,
    phone: false,
  });

  const [fleet, setFleet] = useState(() => FleetService.getBoatsByCategory());
  const [showFleet, setShowFleet] = useState(false);
  const [catName, setCatName] = useState("");

  // 🔥 NEW: Validation error states
  const [bookingCodeError, setBookingCodeError] = useState('');
  const [doubleBookingError, setDoubleBookingError] = useState('');

  // 🔥 NEW (01May2026): Page 5 submission flags from page5_agreement table
  // Source-of-truth for check-in/check-out completion status.
  const [checkInSubmitted, setCheckInSubmitted] = useState(false);
  const [checkOutSubmitted, setCheckOutSubmitted] = useState(false);

  // Load page 5 agreement flags whenever the active booking changes
  useEffect(() => {
    let cancelled = false;
    if (!currentBookingNumber) {
      setCheckInSubmitted(false);
      setCheckOutSubmitted(false);
      return;
    }
    (async () => {
      try {
        const p5 = await getPage5Agreement(currentBookingNumber);
        if (cancelled) return;
        // 🔥 STRICT COMPLETION (06/06): row existence ≠ submit. Το auto-save (page5.php)
        // δημιουργεί ΑΔΕΙΟ check_in/check_out row μόλις ανοίξει η σελ.5. Πραγματικό submit
        // ⇔ email_sent===true Ή items_snapshot!=null (ίδιο κριτήριο με vessel-final-page).
        // ΜΗΝ βάλεις terms_accepted — το γράφει το auto-save χωρίς submit.
        const _submitted = (row: any) => !!row && (row.email_sent === true || row.items_snapshot != null);
        setCheckInSubmitted(_submitted(p5?.checkIn));
        setCheckOutSubmitted(_submitted(p5?.checkOut));
      } catch (e) {
        console.error('Failed to load page5_agreement flags:', e);
        if (!cancelled) {
          setCheckInSubmitted(false);
          setCheckOutSubmitted(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentBookingNumber]);

  // 🔥 MONOHIMEROS/CHECK-IN-DAY GUARD (Problem 1): στην ημέρα check-in, αν ΔΕΝ έχει
  // υποβληθεί (submit) το check-in, εξανάγκασε mode='in'. Διορθώνει landing σε 'out'
  // από saved data / context / location.state, ό,τι κι αν τα έβαλε 'out'.
  // Δεν αγγίζει πολυήμερα στο check-out window (εκεί isCheckInDay=false).
  useEffect(() => {
    if (isEmployee) return;
    if (!form.checkInDate) return;
    const w = getCharterWindow(form.checkInDate, form.checkOutDate);
    if (w.isCheckInDay && !checkInSubmitted && mode === 'out') {
      setMode('in');
      if (updateData) updateData({ mode: 'in' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInSubmitted, form.checkInDate, form.checkOutDate, mode, isEmployee]);

  // 🔒 ONE-SHOT LOCK (29/05) — STAFF: μετά το submit, κλείδωσε mode σε 'out' στο load
  // (ώστε και Next/PDF να δουλεύουν σε check-out, όχι μόνο το toggle).
  useEffect(() => {
    if (!isEmployee) return;
    if (checkInSubmitted && mode !== 'out') {
      setMode('out');
      if (updateData) updateData({ mode: 'out' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmployee, checkInSubmitted, mode]);

  // 🔒 EDGE GUARD — μη-staff σε submitted ναύλο ΔΕΝ μένει στη σελίδα 1 (check-out μόνο
  // από staff). ΘΩΡΑΚΙΣΗ: redirect ΜΟΝΟ αφού «λυθεί» το auth (authResolved), ώστε να
  // ΜΗΝ πεταχτεί ΠΟΤΕ υπάλληλος όσο φορτώνει το auth.
  useEffect(() => {
    if (authResolved && !isEmployee && checkInSubmitted) {
      navigate('/home');
    }
  }, [authResolved, isEmployee, checkInSubmitted, navigate]);

  // 🔥 Helper: Find category for a vessel name from fleet
  const findCategoryForVessel = (vesselName: string, fleetData: any) => {
    if (!vesselName || !fleetData) return null;
    const vesselLower = vesselName.toLowerCase();

    for (const [category, vessels] of Object.entries(fleetData)) {
      if (Array.isArray(vessels)) {
        for (const v of vessels) {
          // Check if vessel name matches (case-insensitive)
          if (v.toLowerCase() === vesselLower ||
              vesselLower.includes(v.toLowerCase()) ||
              v.toLowerCase().includes(vesselLower)) {
            console.log('📍 Found category for vessel:', vesselName, '→', category);
            return { category, vesselName: v }; // Return exact vessel name from fleet
          }
        }
      }
    }
    return null;
  };

// 🔥 AUTO-LOAD booking from HomePage
  useEffect(() => {
    if (location.state?.bookingCode) {
      const bookingCode = location.state.bookingCode;
      console.log('📂 Loading booking from HomePage:', bookingCode);

      const loadData = async () => {
        const data = await loadBookingData(bookingCode);
        if (data) {
          setCurrentBookingNumber(bookingCode);
          let formData = { ...data, status: data.status || 'Draft' };

          // 🔥 AUTO-POPULATE: Find category for vessel if missing
          if (formData.vesselName && !formData.vesselCategory) {
            const match = findCategoryForVessel(formData.vesselName, fleet);
            if (match) {
              formData.vesselCategory = match.category;
              formData.vesselName = match.vesselName;
              console.log('📍 Auto-populated category from HomePage:', match.category);
            }
          }

          setForm(formData);  // 🔥 Default to Draft
          setLastSavedForm(JSON.stringify(formData));  // 🔥 Mark as saved
          setHasUnsavedChanges(false);
          // 🔥 AUTO-MODE: if check-out window, force 'out' — no user override
          const _auto527 = computeAutoMode(data.checkInDate, data.checkOutDate);
          const _stateMode527 = (location?.state as any)?.mode;
          setMode(_auto527 === 'out' ? 'out' : (_stateMode527 || _auto527 || data.mode || 'in'));
        }
      };
      loadData();
    }
  }, [location.state, fleet]);

  // 🔥 CRITICAL FIX: Load data when booking number or mode changes
  useEffect(() => {
    console.log('🔄 Loading data for booking:', currentBookingNumber);

    if (currentBookingNumber) {
      const loadData = async () => {
        const data = await loadBookingData(currentBookingNumber);
        if (data) {
          console.log('✅ Data loaded successfully');
          let formData = { ...data, status: data.status || 'Draft' };

          // 🔥 AUTO-POPULATE: If vesselName exists but category is missing/wrong, find the correct category
          if (formData.vesselName && !formData.vesselCategory) {
            const match = findCategoryForVessel(formData.vesselName, fleet);
            if (match) {
              formData.vesselCategory = match.category;
              formData.vesselName = match.vesselName; // Use exact name from fleet
              console.log('📍 Auto-populated category:', match.category, 'for vessel:', match.vesselName);
            }
          }
          // Also check if category exists but vessel isn't in that category's list
          else if (formData.vesselName && formData.vesselCategory) {
            const categoryVessels = fleet?.[formData.vesselCategory] || [];
            const vesselInCategory = categoryVessels.some(
              (v: string) => v.toLowerCase() === formData.vesselName.toLowerCase()
            );
            if (!vesselInCategory) {
              // Vessel not in specified category, try to find correct category
              const match = findCategoryForVessel(formData.vesselName, fleet);
              if (match) {
                formData.vesselCategory = match.category;
                formData.vesselName = match.vesselName;
                console.log('📍 Corrected category:', match.category, 'for vessel:', match.vesselName);
              }
            }
          }

          setForm(formData);  // 🔥 Default to Draft
          setLastSavedForm(JSON.stringify(formData));  // 🔥 Mark as saved
          setHasUnsavedChanges(false);
          // Don't override mode from data - keep current mode
        } else {
          console.log('⚠️ No data found for this booking');
        }
      };
      loadData();
    }
  }, [currentBookingNumber, fleet]);
  
  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus('online');
      console.log('🌐 Online');
    };
    
    const handleOffline = () => {
      setNetworkStatus('offline');
      console.log('📴 Offline');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const code = currentBookingNumber || form.bookingNumber || '';
      if (!code) return;
      const syncData = await getBookingSync(code);
      if (syncData && syncData.lastUpdatedBy === 'fleetManagement') {
        const page1Data = syncToPage1Format(syncData);
        if (page1Data) {
          setForm(prev => ({
            ...prev,
            bookingNumber: prev.bookingNumber || page1Data.bookingNumber,
            vesselName: prev.vesselName || page1Data.vesselName,
            checkInDate: prev.checkInDate || page1Data.checkInDate,
            checkInTime: prev.checkInTime || page1Data.checkInTime,
            checkOutDate: prev.checkOutDate || page1Data.checkOutDate,
            checkOutTime: prev.checkOutTime || page1Data.checkOutTime,
            skipperFirstName: prev.skipperFirstName || page1Data.skipperFirstName || '',
            skipperLastName: prev.skipperLastName || page1Data.skipperLastName || '',
            skipperAddress: prev.skipperAddress || page1Data.skipperAddress || '',
            skipperEmail: prev.skipperEmail || page1Data.skipperEmail || '',
            skipperPhone: prev.skipperPhone || page1Data.skipperPhone || '',
            chartererFirstName: prev.chartererFirstName || page1Data.chartererFirstName || '',
            chartererLastName: prev.chartererLastName || page1Data.chartererLastName || '',
            chartererAddress: prev.chartererAddress || page1Data.chartererAddress || '',
            chartererEmail: prev.chartererEmail || page1Data.chartererEmail || '',
            chartererPhone: prev.chartererPhone || page1Data.chartererPhone || '',
            sameAsSkipper: false,
          }));
        }
      }
    })();
  }, []);


  // 🔥 FIX: When bookingNumber exists but skipper fields are empty, fetch from API
  useEffect(() => {
    // 🔥 BUG 2 FIX: guard on chartererFirstName (page1.php returns only skipper, charterer needs bookings.php)
    if (!form.bookingNumber || form.chartererFirstName) return;

    const fetchBookingData = async () => {
      try {
        console.log('📥 PAGE 1: Fetching booking data for:', form.bookingNumber);
        const response = await fetch(`https://yachtmanagementsuite.com/api/bookings.php?booking_number=${encodeURIComponent(form.bookingNumber)}`);
        const data = await response.json();

        if (data.booking?.booking_data) {
          const bd = data.booking.booking_data;
          console.log('📥 PAGE 1: Got booking_data from API:', bd);

          setForm(prev => {
            const skipperFirst = bd.skipperFirstName || '';
            const skipperLast = bd.skipperLastName || '';
            const skipperAddr = bd.skipperAddress || '';
            const skipperMail = bd.skipperEmail || '';
            const skipperPh = bd.skipperPhone || '';
            const isSame = bd.sameAsSkipper !== undefined ? bd.sameAsSkipper : false;

            // Τα Dob/Gender/Nationality ΔΕΝ αποθηκεύονται ποτέ flat (το save :224-228 τα πετάει)
            // και συχνά λείπουν και nested. ΤΡΙΤΟ fallback: η γραμμή του crew list, όπου
            // πράγματι υπάρχουν. Σειρά: flat -> nested -> crewMembers. READ-ONLY — καμία
            // εγγραφή, καμία migration, κανένα sync δύο αντιγράφων.
            const crewArr = Array.isArray(bd.crewMembers) ? bd.crewMembers : [];
            const chCrew = crewArr.find((m) => m && m.isCharterer === true) || {};
            const skCrew = crewArr.find((m) => String((m && m.role) || '').toLowerCase() === 'skipper') || {};

            return {
              ...prev,
              skipperFirstName: prev.skipperFirstName || skipperFirst,
              skipperLastName: prev.skipperLastName || skipperLast,
              skipperAddress: prev.skipperAddress || skipperAddr,
              skipperEmail: prev.skipperEmail || skipperMail,
              skipperPhone: prev.skipperPhone || skipperPh,
              chartererFirstName: prev.chartererFirstName || (isSame ? skipperFirst : (bd.chartererFirstName || '')),
              chartererLastName: prev.chartererLastName || (isSame ? skipperLast : (bd.chartererLastName || '')),
              chartererAddress: prev.chartererAddress || (isSame ? skipperAddr : (bd.chartererAddress || '')),
              chartererEmail: prev.chartererEmail || (isSame ? skipperMail : (bd.chartererEmail || '')),
              chartererPhone: prev.chartererPhone || (isSame ? skipperPh : (bd.chartererPhone || '')),
              chartererDob: prev.chartererDob || bd.chartererDob || bd.chartererData?.dob || chCrew.dateOfBirth || '',
              chartererGender: prev.chartererGender || bd.chartererGender || bd.chartererData?.gender || chCrew.gender || '',
              chartererNationality: prev.chartererNationality || bd.chartererNationality || bd.chartererData?.nationality || chCrew.nationality || '',
              /* ΤΥΠΟΣ ΝΑΥΛΩΤΗ: flat (prod) ή nested (online) — και τα δύο σχήματα. */
              chartererIsCompany: prev.chartererIsCompany || bd.chartererIsCompany || bd.chartererData?.isCompany || false,
              chartererCompanyName: prev.chartererCompanyName || bd.chartererCompanyName || bd.chartererData?.companyName || '',
              chartererCountry: prev.chartererCountry || bd.chartererCountry || bd.chartererData?.country || '',
              skipperDob: prev.skipperDob || bd.skipperDob || bd.chartererData?.skipperDob || skCrew.dateOfBirth || '',
              skipperGender: prev.skipperGender || bd.skipperGender || bd.chartererData?.skipperGen || skCrew.gender || '',
              skipperNationality: prev.skipperNationality || bd.skipperNationality || bd.chartererData?.skipperNationality || skCrew.nationality || '',
              sameAsSkipper: isSame,
            };
          });
        }
      } catch (error) {
        console.error('❌ PAGE 1: Error fetching booking data:', error);
      }
    };

    fetchBookingData();
  }, [form.bookingNumber, form.chartererFirstName]);

  // Load skipper from charter-archive (CharterAgreement invitations) or crew-invitations directly
  useEffect(() => {
    if (!form.bookingNumber) return;
    if (form.skipperFirstName || form.skipperLastName) return;

    (async () => {
      try {
        const cd: any = await loadChartererData(form.bookingNumber);
        if (cd && (cd.skipperFirstName || cd.skipperLastName)) {
          console.log('📥 PAGE 1: Loading skipper from charter-archive');
          setForm(prev => ({
            ...prev,
            skipperFirstName: prev.skipperFirstName || cd.skipperFirstName || '',
            skipperLastName: prev.skipperLastName || cd.skipperLastName || '',
            skipperAddress: prev.skipperAddress || cd.skipperAddress || '',
            skipperEmail: prev.skipperEmail || cd.skipperEmail || '',
            skipperPhone: prev.skipperPhone || cd.skipperPhone || '',
            sameAsSkipper: false,
          }));
          return;
        }

        const r = await fetch('/api/crew-invitations.php?action=list&booking_number=' + encodeURIComponent(form.bookingNumber));
        const j = await r.json();
        if (j?.success && Array.isArray(j.data)) {
          const sk = j.data.find((i: any) => i.role === 'skipper' && i.status === 'submitted');
          if (sk) {
            console.log('📥 PAGE 1: Loading skipper from crew-invitations');
            setForm(prev => ({
              ...prev,
              skipperFirstName: prev.skipperFirstName || sk.submitted_first_name || '',
              skipperLastName: prev.skipperLastName || sk.submitted_last_name || '',
              skipperAddress: prev.skipperAddress || sk.submitted_address || '',
              skipperEmail: prev.skipperEmail || sk.submitted_email || sk.invite_email || '',
              skipperPhone: prev.skipperPhone || sk.submitted_phone || '',
              sameAsSkipper: false,
            }));
          }
        }
      } catch (e) {
        console.error('❌ PAGE 1: Skipper enrichment failed', e);
      }
    })();
  }, [form.bookingNumber]);

  // 🔥 TWO-WAY SYNC: Save form data to sync storage whenever it changes
  useEffect(() => {
    // Debug: Log exactly what we're trying to save
    console.log('🔄 PAGE1 SYNC - Form values:', {
      bookingNumber: form.bookingNumber,
      vesselName: form.vesselName,
      checkInDate: form.checkInDate,
      checkOutDate: form.checkOutDate,
      skipperFirstName: form.skipperFirstName,
      skipperLastName: form.skipperLastName
    });

    // Only save if we have meaningful data
    if (form.bookingNumber || form.vesselName || form.checkInDate) {
      const syncData = page1ToSyncFormat(form);
      console.log('🔄 PAGE1 SYNC - Converted syncData:', syncData);
      saveBookingSync(syncData, 'page1');
    }
  }, [form.bookingNumber, form.vesselName, form.checkInDate, form.checkInTime,
      form.checkOutDate, form.checkOutTime, form.skipperFirstName, form.skipperLastName,
      form.skipperAddress, form.skipperEmail, form.skipperPhone]);

  useEffect(() => {
    const newHistory = { ...history };
    let hasChanges = false;

    if (form.bookingNumber && !history.bookingNumbers.includes(form.bookingNumber)) {
      newHistory.bookingNumbers = [form.bookingNumber, ...history.bookingNumbers].slice(0, 20);
      hasChanges = true;
    }
    if (form.skipperFirstName && !history.firstNames.includes(form.skipperFirstName)) {
      newHistory.firstNames = [form.skipperFirstName, ...history.firstNames].slice(0, 20);
      hasChanges = true;
    }
    if (form.skipperLastName && !history.lastNames.includes(form.skipperLastName)) {
      newHistory.lastNames = [form.skipperLastName, ...history.lastNames].slice(0, 20);
      hasChanges = true;
    }
    if (form.skipperAddress && !history.addresses.includes(form.skipperAddress)) {
      newHistory.addresses = [form.skipperAddress, ...history.addresses].slice(0, 20);
      hasChanges = true;
    }
    if (form.skipperEmail && !history.emails.includes(form.skipperEmail)) {
      newHistory.emails = [form.skipperEmail, ...history.emails].slice(0, 20);
      hasChanges = true;
    }
    if (form.skipperPhone && !history.phones.includes(form.skipperPhone)) {
      newHistory.phones = [form.skipperPhone, ...history.phones].slice(0, 20);
      hasChanges = true;
    }

    if (hasChanges) {
      setHistory(newHistory);
    }
  }, [form, history]);

  const handleEmployeeLogin = () => {
    const user = authService.login(employeeCode);
    if (user) {
      setCurrentEmployee(user.permissions);
      setIsEmployee(true);
      setShowLoginModal(false);
      alert(`${lang === 'el' ? 'Καλωσήρθες' : 'Welcome'} ${user.name}!`);
      setEmployeeCode("");
    } else {
      alert(lang === 'el' ? 'Λάθος κωδικός!' : 'Wrong code!');
      setEmployeeCode("");
    }
  };
  
  const handleEmployeeLogout = () => {
    setIsEmployee(false);
    setCurrentEmployee(null);
    alert(lang === 'el' ? 'Αποσυνδεθήκατε!' : 'Logged out!');
  };

  const handleToggleMode = () => {
    // 🔒 ONE-SHOT LOCK (29/05) — STAFF: submitted check-in → toggle ανενεργό, μένει check-out.
    // Return ΠΡΙΝ όλα τα guards → κανένα «Check-in not available»/«complete check-in first».
    if (isEmployee && checkInSubmitted) {
      if (mode !== 'out') { setMode('out'); if (updateData) updateData({ mode: 'out' }); }
      return;
    }

    // 🔥 BEFORE-CHARTER GUARD — αν η κράτηση δεν έχει ξεκινήσει (σήμερα < check-in),
    // ΚΑΘΕ toggle δίνει ΣΩΣΤΟ check-in μήνυμα (όχι παραπλανητικό check-out window).
    if (form.checkInDate) {
      const wTog = getCharterWindow(form.checkInDate, form.checkOutDate);
      if (wTog.isBeforeCheckIn) {
        const msg = lang === 'el'
          ? `❌ Η κράτηση δεν έχει ξεκινήσει. Το Check-in γίνεται την ημέρα του ναύλου: ${formatDate(form.checkInDate)}`
          : `❌ The charter hasn't started yet. Check-in is on the charter day: ${formatDate(form.checkInDate)}`;
        alert(msg);
        return;
      }
    }

    const newMode = mode === 'in' ? 'out' : 'in';

    // 🔥 NEW (01May2026): Page 5 submission gating
    // Rule: check-out cannot be opened until check-in has been submitted on Page 5.
    if (!isEmployee) {
      // Guard A: Toggle to 'out' requires check-in to be already submitted
      if (newMode === 'out' && !checkInSubmitted) {
        const msg = lang === 'el'
          ? '❌ Πρώτα ολοκληρώστε το Check-in (υποβολή Σελίδας 5). Μετά μπορείτε να ξεκινήσετε Check-out.'
          : '❌ Please complete the Check-in first (submit Page 5). Then you can start Check-out.';
        alert(msg);
        return;
      }
      // Guard B: Toggle to 'in' after BOTH check-in and check-out have been submitted is forbidden
      if (newMode === 'in' && checkInSubmitted && checkOutSubmitted) {
        const msg = lang === 'el'
          ? '❌ Αυτή η κράτηση έχει ολοκληρωθεί (Check-in + Check-out). Δεν μπορείτε να αλλάξετε mode.'
          : '❌ This booking is fully completed (Check-in + Check-out). Mode change not allowed.';
        alert(msg);
        return;
      }
      // 🔥 PROBLEM 2: μονοήμερο + check-in submitted + ήδη σε check-out.
      // Το 2ο πάτημα του toggle υπολογίζει 'in' και θα χτυπούσε το Guard #1
      // («Check-in not available»). Κράτα τον χρήστη σε check-out (no-op).
      if (newMode === 'in' && mode === 'out' && checkInSubmitted && !checkOutSubmitted) {
        const wMono = getCharterWindow(form.checkInDate, form.checkOutDate);
        if (wMono.monohimeros) return;
      }
    }

    // 🔥 HARD LOCK: if auto-mode says 'out' (check-out window), ANY toggle to 'in' is forbidden
    const _autoToggle = computeAutoMode(form.checkInDate, form.checkOutDate);
    if (_autoToggle === 'out' && newMode === 'in') {
      const msg = lang === 'el'
        ? '❌ Το Check-in δεν είναι διαθέσιμο — η κράτηση βρίσκεται σε check-out window.'
        : '❌ Check-in not available — booking is in check-out window.';
      alert(msg);
      return;
    }

    // 🔥 TZ-SAFE guards for BOTH directions — local midnight parsing, no UTC bugs
    {
      const _parseLocal = (s: string): Date => {
        const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
        if (y && m && d) return new Date(y, m - 1, d);
        return new Date(s);
      };
      const _ymdLocal = (d: Date): string =>
        `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

      const n = new Date();
      const today = new Date(n.getFullYear(), n.getMonth(), n.getDate());
      const ciDate = form.checkInDate ? _parseLocal(form.checkInDate) : null;
      const coDate = form.checkOutDate ? _parseLocal(form.checkOutDate) : null;
      const dayBefore = coDate ? new Date(coDate.getFullYear(), coDate.getMonth(), coDate.getDate() - 1) : null;

      const isCheckOutWindow = !!(coDate && dayBefore) && (today.getTime() === coDate.getTime() || today.getTime() === dayBefore.getTime());
      const isCheckInDay = !!ciDate && today.getTime() === ciDate.getTime();
      const isMonohimeros = !!(ciDate && coDate) && ciDate.getTime() === coDate.getTime();

      // Guard #1: Toggle to 'in' — BLOCKED when we're in check-out window
      if (newMode === 'in' && isCheckOutWindow) {
        const msg = lang === 'el'
          ? `❌ Το Check-in δεν είναι διαθέσιμο — η κράτηση βρίσκεται σε check-out window.\n\nΗμερομηνία Check-out: ${formatDate(form.checkOutDate)}\nΣήμερα: ${formatDate(_ymdLocal(today))}`
          : `❌ Check-in not available — booking is in check-out window.\n\nCheck-out date: ${formatDate(form.checkOutDate)}\nToday: ${formatDate(_ymdLocal(today))}`;
        alert(msg);
        return;
      }

      // Guard #2: Toggle to 'out' — BLOCKED when we're on check-in day
      if (newMode === 'out' && isCheckInDay && !isMonohimeros && !isEarlyCheckoutBooking()) {
        const msg = lang === 'el'
          ? (isMonohimeros
              ? `❌ Το Check-out για μονοήμερο ναύλο ανοίγει μετά το submit του check-in.`
              : `❌ Το Check-out είναι διαθέσιμο μόνο στις 2 τελευταίες ημέρες του ναύλου.\n\nΗμερομηνία Check-out: ${formatDate(form.checkOutDate)}\nΔιαθέσιμο: ${dayBefore ? formatDate(_ymdLocal(dayBefore)) : ''} & ${formatDate(form.checkOutDate)}\nΣήμερα: ${formatDate(_ymdLocal(today))}`)
          : (isMonohimeros
              ? `❌ Check-out for a day charter opens after the check-in is submitted.`
              : `❌ Check-out is only available on the last 2 days of the charter.\n\nCheck-out date: ${formatDate(form.checkOutDate)}\nAvailable: ${dayBefore ? formatDate(_ymdLocal(dayBefore)) : ''} & ${formatDate(form.checkOutDate)}\nToday: ${formatDate(_ymdLocal(today))}`);
        alert(msg);
        return;
      }

      // Guard #3: Toggle to 'out' — BLOCKED when outside check-out window (not start day, not in window)
      if (newMode === 'out' && form.checkOutDate && !isCheckOutWindow && !isEarlyCheckoutBooking()) {
        const msg = lang === 'el'
          ? (isMonohimeros
              ? `❌ Το Check-out για μονοήμερο ναύλο ανοίγει μετά το submit του check-in.`
              : `❌ Το Check-out είναι διαθέσιμο μόνο στις 2 τελευταίες ημέρες του ναύλου.\n\nΗμερομηνία Check-out: ${formatDate(form.checkOutDate)}\nΔιαθέσιμο: ${dayBefore ? formatDate(_ymdLocal(dayBefore)) : ''} & ${formatDate(form.checkOutDate)}\nΣήμερα: ${formatDate(_ymdLocal(today))}`)
          : (isMonohimeros
              ? `❌ Check-out for a day charter opens after the check-in is submitted.`
              : `❌ Check-out is only available on the last 2 days of the charter.\n\nCheck-out date: ${formatDate(form.checkOutDate)}\nAvailable: ${dayBefore ? formatDate(_ymdLocal(dayBefore)) : ''} & ${formatDate(form.checkOutDate)}\nToday: ${formatDate(_ymdLocal(today))}`);
        alert(msg);
        return;
      }
    }

    // 🔥 FIX: Use functional update to get latest form state (avoids stale closure)
    setForm(prevForm => {
      const updatedForm = { ...prevForm, mode: newMode };

      // Save with the updated form data
      if (currentBookingNumber) {
        saveBookingData(currentBookingNumber, updatedForm);
      }

      return updatedForm;
    });

    setMode(newMode);

    if (updateData) {
      updateData({ mode: newMode });
    }

    window.dispatchEvent(new CustomEvent('modeChanged', {
      detail: { mode: newMode }
    }));

    console.log('🔄 Mode changed to:', newMode);
  };

  const handleEarlyCheckout = async (e: any, bookingCode: string) => {
    e.stopPropagation();
    // Early/εκτός-ημέρας check-out: TECHNICAL/BASE_MANAGER (στη βάση με τον πελάτη) + ADMIN.
    if (!['ADMIN', 'TECHNICAL', 'BASE_MANAGER'].includes(currentEmployee?.role || '')) return;
    const _cur = (globalBookings || []).find((x: any) => { const _c = x.bookingCode || x.code || x.bookingNumber || x.charterCode || ''; const _t = extractCode(bookingCode); return _c === bookingCode || (_t && extractCode(_c) === _t); });
    const _isUnlock = !(_cur && _cur.early_checkout);
    const msg = _isUnlock ? (lang === 'el' ? 'Προωρο Check-out για ' + bookingCode + ';' : 'Early Check-out for ' + bookingCode + '?') : (lang === 'el' ? 'Κλειδωμα (αναιρεση προωρου) για ' + bookingCode + ';' : 'Lock (undo early) for ' + bookingCode + '?');
    if (!window.confirm(msg)) return;
    try {
      const _tc = extractCode(bookingCode);
      const _match = (globalBookings || []).find((x: any) => {
        const _c = x.bookingCode || x.code || x.bookingNumber || x.charterCode || '';
        return _c === bookingCode || (_tc && extractCode(_c) === _tc);
      });
      const realCode = (_match && (_match.bookingNumber || _match.bookingCode || _match.code || _match.charterCode)) || bookingCode;
      const res = await getBooking(realCode);
      const existing = (res && res.booking && res.booking.booking_data) ? res.booking.booking_data : {};
      const _newFlag = !(existing && existing.early_checkout);
      const merged = { ...existing, early_checkout: _newFlag };
      await updateBooking(realCode, { bookingData: merged });
      if (refreshBookings) await refreshBookings();
      handleSelectBooking(bookingCode);
      setMode(_newFlag ? 'out' : 'in');
      if (updateData) updateData({ mode: _newFlag ? 'out' : 'in' });
    } catch (err) {
      alert(lang === 'el' ? 'Σφαλμα προωρου check-out' : 'Early check-out error');
    }
  };

  const isEarlyCheckoutBooking = () => {
    const _bn = currentBookingNumber || form.bookingNumber;
    if (!_bn) return false;
    const _tc = extractCode(_bn);
    const _b = (globalBookings || []).find((x: any) => {
      const _c = x.bookingCode || x.code || x.bookingNumber || x.charterCode || '';
      return _c === _bn || (_tc && extractCode(_c) === _tc);
    });
    return !!(_b && _b.early_checkout);
  };

  // 📧 Resend button visual feedback: status ανά booking code ('sending'|'sent'|'error')
  const [resendStatus, setResendStatus] = useState({});

  // 📧 RESEND CHECK-IN EMAIL από τη λίστα (staff-only). Ξαναστέλνει το check-in email
  // (HTML σώμα + PDF attachment) για το συγκεκριμένο booking, ανακατασκευάζοντας τα πάντα
  // από τη DB: booking_data (getBooking) + authoritative υπογραφές + frozen items_snapshot
  // (getPage5Agreement). ΚΑΜΙΑ DB write — δεν αγγίζει υπογραφές/snapshot/check-in.
  const handleResendFromList = async (bookingCode: string) => {
    const _finishResend = (st) => {
      setResendStatus((prev) => ({ ...prev, [bookingCode]: st }));
      setTimeout(() => setResendStatus((prev) => { const n = { ...prev }; delete n[bookingCode]; return n; }), 3000);
    };
    setResendStatus((prev) => ({ ...prev, [bookingCode]: 'sending' }));
    try {
      // 1) booking_data (emails / vessel / dates)
      const raw = await getBooking(bookingCode);
      const bd = raw?.booking?.booking_data || raw?.booking_data || raw || {};

      // 2) page5_agreement check-in row (authoritative signatures + frozen snapshot)
      const ag = await getPage5Agreement(bookingCode);
      const row = ag?.checkIn;
      if (!row) {
        _finishResend('error');
        return;
      }

      // 3) flatten frozen items_snapshot -> allItems shape που θέλει το generateLuxuryPDF (isPage5)
      const PAGE_LABELS = { page2: 'Page 2', page3: 'Page 3', page4: 'Page 4' };
      const flatItems = [];
      const snap = row.items_snapshot;
      if (snap && typeof snap === 'object') {
        for (const pageKey of ['page2', 'page3', 'page4']) {
          const sections = snap[pageKey];
          if (!sections || typeof sections !== 'object') continue;
          for (const sectionKey of Object.keys(sections)) {
            const items = sections[sectionKey];
            if (!Array.isArray(items)) continue;
            for (const it of items) {
              const labels = it.labels || {};
              flatItems.push({
                page: PAGE_LABELS[pageKey] || pageKey,
                section: sectionKey,
                nameEn: labels.en || it.item_key || '',
                name: labels[lang] || labels.en || it.item_key || '',
                qty: it.qty != null ? it.qty : 1,
                inOk: !!it.inOk,
                out: it.out != null ? it.out : null,
                price: it.price != null ? it.price : 0,
                sort_order: it.sort_order != null ? it.sort_order : null,
              });
            }
          }
        }
      }

      // 4a) check-in duration text (αν υπάρχει στο frozen snapshot) — ΙΔΙΑ logic με vessel-final-page
      let checkInDurationText = '';
      const durMin = snap && snap.checkInDuration && snap.checkInDuration.durationMinutes;
      if (typeof durMin === 'number' && durMin >= 0) {
        const h = Math.floor(durMin / 60);
        const m = durMin % 60;
        const DUR_UNITS: Record<string, [string, string]> = {
          el: ['ώρες', 'λεπτά'], en: ['hours', 'minutes'], it: ['ore', 'minuti'],
          de: ['Stunden', 'Minuten'], ru: ['часов', 'минут'], fr: ['heures', 'minutes'],
          ro: ['ore', 'minute'], pl: ['godzin', 'minut'], he: ['שעות', 'דקות'], es: ['horas', 'minutos'],
        };
        const DUR_TPL: Record<string, string> = {
          el: 'Η διαδικασία check-in διήρκεσε {D} από τον τεχνικό της εταιρείας',
          en: 'The check-in process took {D} with the company technician',
          it: 'La procedura di check-in è durata {D} con il tecnico della compagnia',
          de: 'Der Check-in-Vorgang dauerte {D} mit dem Techniker des Unternehmens',
          ru: 'Процесс регистрации занял {D} с техником компании',
          fr: "La procédure d'enregistrement a duré {D} avec le technicien de la compagnie",
          ro: 'Procesul de check-in a durat {D} cu tehnicianul companiei',
          pl: 'Proces odprawy trwał {D} z technikiem firmy',
          he: "תהליך הצ'ק-אין נמשך {D} עם טכנאי החברה",
          es: 'El proceso de check-in duró {D} con el técnico de la compañía',
        };
        const durLang = DUR_TPL[lang] ? lang : 'en';
        const [hUnit, mUnit] = DUR_UNITS[durLang];
        const dur = h > 0 ? (h + ' ' + hUnit + ' ' + m + ' ' + mUnit) : (m + ' ' + mUnit);
        checkInDurationText = DUR_TPL[durLang].replace('{D}', dur);
      }

      // 4) additionalData — booking submitted => consents ήταν αποδεκτά
      const additionalData = {
        agreements: { terms: true, privacy: true, return: true, warning: true, paymentAuth: false, noDamageRelease: false },
        warningAccepted: true,
        paymentAuthAccepted: false,
        noDamageReleaseAccepted: false,
        notes: row.notes || '',
        skipperSignature: row.skipper_signature || '',
        employeeSignature: row.company_signature || '',
        allItems: flatItems,
        photos: {},
        checkInDurationText,
        timestamp: new Date().toISOString()
      };

      // 5) PDF (isPage5) -> blob
      const pdfDoc = generateLuxuryPDF(bd, 'in', additionalData, lang, { isPage5: true });
      if (!pdfDoc) {
        _finishResend('error');
        return;
      }
      const pdfBlob = pdfDoc.output('blob');

      // 6) recipient (skipper + charterer, dedup) + send (HTML σώμα + PDF μέσω sendCheckInEmail)
      const emailList = [bd.skipperEmail, bd.chartererEmail]
        .map((e) => (e || '').toString().trim())
        .filter((e) => e.length > 0);
      const seen = new Set();
      const customerEmail = emailList.filter((e) => {
        const k = e.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      }).join(',');

      const result = await sendCheckInEmail(customerEmail, bd, pdfBlob, additionalData, lang);
      if (result && result.success) {
        _finishResend('sent');
      } else {
        _finishResend('error');
      }
    } catch (err) {
      console.error('❌ Resend from list error:', err);
      _finishResend('error');
    }
  };

  const handleSelectBooking = (bookingCode: string) => {
    setCurrentBookingNumber(bookingCode);

    // 🔥 FIX: Search by multiple possible code fields
    const targetCode = extractCode(bookingCode);
    const booking = (globalBookings || []).find(b => {
      const bCode = b.bookingCode || b.code || b.bookingNumber || b.charterCode || '';
      return bCode === bookingCode || (targetCode && extractCode(bCode) === targetCode);
    });

    if (booking) {
      // 🔥 FIX: Use fallbacks for all fields to handle both FleetManagement and Page1 formats
      setForm({
        bookingNumber: booking.bookingCode || booking.code || booking.bookingNumber || '',
        vesselCategory: booking.vesselCategory || booking.boatType || booking.vesselType || '',
        vesselName: booking.vesselName || booking.boatName || booking.vessel || '',
        checkInDate: booking.startDate || booking.checkInDate || '',
        checkInTime: booking.checkInTime || booking.startTime || '',
        checkOutDate: booking.endDate || booking.checkOutDate || '',
        checkOutTime: booking.checkOutTime || booking.endTime || '',
        skipperFirstName: booking.skipperFirstName || '',
        skipperLastName: booking.skipperLastName || '',
        skipperAddress: booking.skipperAddress || '',
        skipperEmail: booking.skipperEmail || '',
        skipperPhone: booking.skipperPhone || '',
        phoneCountryCode: booking.phoneCountryCode || '+30',
        vesselId: booking.vesselId || booking.vesselName || booking.boatName || '',
        chartererFirstName: booking.chartererFirstName || '',
        chartererLastName: booking.chartererLastName || '',
        chartererAddress: booking.chartererAddress || '',
        chartererEmail: booking.chartererEmail || '',
        chartererPhone: booking.chartererPhone || '',
        chartererDob: booking.chartererDob || booking.chartererData?.dob || '',
        chartererGender: booking.chartererGender || booking.chartererData?.gender || '',
        chartererNationality: booking.chartererNationality || booking.chartererData?.nationality || '',
        chartererIsCompany: booking.chartererIsCompany || booking.chartererData?.isCompany || false,
        chartererCompanyName: booking.chartererCompanyName || booking.chartererData?.companyName || '',
        chartererCountry: booking.chartererCountry || booking.chartererData?.country || '',
        skipperDob: booking.skipperDob || booking.chartererData?.skipperDob || '',
        skipperGender: booking.skipperGender || booking.chartererData?.skipperGen || '',
        skipperNationality: booking.skipperNationality || booking.chartererData?.skipperNationality || '',
        sameAsSkipper: false,
      });
      const _auto914 = computeAutoMode(booking.startDate || booking.checkInDate, booking.endDate || booking.checkOutDate);
      const _stateMode914 = (location?.state as any)?.mode;
      setMode((_auto914 === 'out' || booking.early_checkout) ? 'out' : (_stateMode914 || _auto914 || booking.mode || 'in'));
    }

    setShowBookingSelector(false);
    setShowAllBookings(false);
  };
  
  const handleClearAllData = () => {
    if (!currentEmployee?.canClearData) {
      alert(lang === 'el' ? 'Δεν έχετε δικαίωμα!' : 'No permission!');
      return;
    }
    
    if (window.confirm(t.confirmClearAll)) {
      // Clear state only - no localStorage for bookings
      setCurrentBookingNumber('');
      setForm({
        bookingNumber: "",
        vesselCategory: "",
        vesselName: "",
        checkInDate: "",
        checkInTime: "",
        checkOutDate: "",
        checkOutTime: "",
        skipperFirstName: "",
        skipperLastName: "",
        skipperAddress: "",
        skipperEmail: "",
        skipperPhone: "",
        chartererDob: "", chartererGender: "", chartererNationality: "", skipperDob: "", skipperGender: "", skipperNationality: "",
        phoneCountryCode: "+30",
      });
      setMode('in');
      setDuplicateBooking(null);
      
      if (updateData) {
        updateData({ bookingNumber: '', mode: 'in' });
      }
      
      alert(t.allDataCleared);
      console.log('🗑️ All data cleared');
    }
  };

  // 🔥 NEW: Load existing booking from duplicate warning
  const handleLoadExistingBooking = () => {
    if (!duplicateBooking) return;
    
    const { bookingNumber, data } = duplicateBooking;
    
    setCurrentBookingNumber(bookingNumber);
    setForm(data);
    const _auto965 = computeAutoMode(data.checkInDate, data.checkOutDate);
    const _stateMode965 = (location?.state as any)?.mode;
    setMode(_auto965 === 'out' ? 'out' : (_stateMode965 || _auto965 || data.mode || 'in'));
    setDuplicateBooking(null);
    
    // Update context
    if (updateData) {
      updateData({ 
        bookingNumber: bookingNumber,
        mode: data.mode || 'in'
      });
    }
    
    // Dispatch event
    window.dispatchEvent(new CustomEvent('bookingChanged', { 
      detail: { bookingNumber: bookingNumber } 
    }));
    
    console.log('✅ Loaded existing booking:', bookingNumber);
    
    alert(lang === 'el' 
      ? `✅ Φορτώθηκε το υπάρχον booking: ${bookingNumber}` 
      : `✅ Loaded existing booking: ${bookingNumber}`);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (mode === 'out' && name !== 'mode') {
      return;
    }

    if (name === 'checkOutDate' && value) {
      if (form.checkInDate && value < form.checkInDate) {
        alert(lang === 'el' ?
          'Η ημερομηνία Check-out δεν μπορεί να είναι πριν από το Check-in!' :
          'Check-out date cannot be before Check-in date!');
        return;
      }
      // Check double booking when checkout date changes
      if (form.vesselName && form.checkInDate && value) {
        await checkDoubleBooking(form.vesselName, form.checkInDate, value);
      }
    }

    if (name === 'checkInDate' && value) {
      if (form.checkOutDate && value > form.checkOutDate) {
        setForm(prev => ({
          ...prev,
          [name]: value,
          checkOutDate: '',
          checkOutTime: ''
        }));
        alert(lang === 'el' ?
          'Το Check-out επαναφέρθηκε γιατί ήταν πριν το νέο Check-in!' :
          'Check-out was reset because it was before the new Check-in!');
        return;
      }
      // Check double booking when checkin date changes
      if (form.vesselName && value && form.checkOutDate) {
        await checkDoubleBooking(form.vesselName, value, form.checkOutDate);
      }
    }

    if (name === 'vesselName' && value) {
      // 🔥 FIX: Use vesselName directly (not transformed) for localStorage key matching
      // AdminDashboard checks: fleet_{boat.name}_ΝΑΥΛΑ, fleet_{UPPER}_ΝΑΥΛΑ, fleet_{lower}_ΝΑΥΛΑ
      // So we should save with the exact vesselName to ensure matching
      const vesselId = value; // Use exact vessel name for storage key matching

      console.log('🔥 PAGE 1: Setting vessel ID:', vesselId);
      console.log('🔥 PAGE 1: Storage key will be: fleet_' + vesselId + '_ΝΑΥΛΑ');
      localStorage.setItem('selectedVessel', vesselId);

      // 🔥 FIX: Also set vesselId in form so saveToFleetManagementStorage receives it
      setForm(prev => ({
        ...prev,
        [name]: value,
        vesselId: vesselId
      }));

      // Check double booking when vessel changes
      if (value && form.checkInDate && form.checkOutDate) {
        await checkDoubleBooking(value, form.checkInDate, form.checkOutDate);
      }

      return; // Don't call setForm again below
    }

    if (name === 'bookingNumber' && value) {
      setCurrentBookingNumber(value);

      // 🔥 Check if booking number exists in API (API is source of truth)
      if (value.trim()) {
        await checkDuplicateBookingCode(value.trim());
      }
    } else if (name === 'bookingNumber' && !value) {
      setDuplicateBooking(null);
      setBookingCodeError('');
    }

    setForm(prev => ({ ...prev, [name]: value }));
  };

  // 🔥 API VALIDATION: Check if booking code already exists (uses imported checkDuplicateCharterCode)
  const checkDuplicateBookingCode = async (code: string) => {
    if (!code || !code.trim()) {
      setBookingCodeError('');
      return;
    }

    try {
      const excludeId = currentBookingNumber || form.bookingNumber;
      const result = await checkDuplicateCharterCode(code, excludeId);

      if (result.isDuplicate) {
        setBookingCodeError(lang === 'el' ?
          'Αυτός ο αριθμός ναύλου υπάρχει ήδη' :
          'This charter party number already exists');
      } else {
        setBookingCodeError('');
      }
    } catch (error) {
      console.error('Error checking duplicate booking code:', error);
    }
  };

  // 🔥 SERVER VALIDATION: ο έλεγχος διπλοκράτησης γίνεται ΑΠΟΚΛΕΙΣΤΙΚΑ στον server
  // (bookings.php?action=check_overlap). Στέλνουμε το ΟΝΟΜΑ του σκάφους όπως το
  // επέλεξε ο χρήστης και ο server το λύνει από τον πίνακα vessels. Δεν ψάχνουμε
  // πια id σε τοπική λίστα (localStorage FLEET_STORAGE_KEY): εκεί ακριβώς παλιώνει
  // και ο έλεγχος έπεφτε σε άλλο σκάφος — ίδιος ναύλος περνούσε από laptop και
  // μπλοκαριζόταν από PC. Επίσης, όταν το σκάφος δεν βρισκόταν στη λίστα, ο έλεγχος
  // σιωπηλά ΔΕΝ γινόταν καθόλου.
  const checkDoubleBooking = async (vessel: string, startDate: string, endDate: string) => {
    if (!vessel || !startDate || !endDate) {
      setDoubleBookingError('');
      return;
    }

    const excludeId = currentBookingNumber || form.bookingNumber;
    const result = await checkDateOverlap(undefined, startDate, endDate, excludeId, vessel);

    if (result.error) {
      // Ο server δεν αποφάνθηκε — το λέμε καθαρά, δεν κρίνουμε εδώ.
      setDoubleBookingError(result.error);
      return;
    }

    if (result.hasOverlap) {
      const c: any = result.overlappingBooking || {};
      const ref = `${c.bookingNumber || c.code || ''} (${c.startDate} → ${c.endDate})`.trim();
      setDoubleBookingError(lang === 'el' ?
        `Το σκάφος έχει ήδη κράτηση: ${ref}` :
        `This vessel already has a booking: ${ref}`);
    } else {
      setDoubleBookingError('');
    }
  };

  const getFilteredSuggestions = (fieldName, value) => {
    const fieldMap = {
      bookingNumber: history.bookingNumbers,
      skipperFirstName: history.firstNames,
      skipperLastName: history.lastNames,
      skipperAddress: history.addresses,
      skipperEmail: history.emails,
      skipperPhone: history.phones,
    };
    
    const suggestions = fieldMap[fieldName] || [];
    
    if (!value || value.length === 0) {
      return suggestions;
    }
    
    return suggestions.filter(item => 
      item.toLowerCase().includes(value.toLowerCase())
    );
  };

  const brand = {
    black: "#000000",
    blue: "#3B82F6",
    successBorder: "#22c55e",
    successBg: "#d1fae5",
    pageBg: "#eae8dc",
  };
  
  const filledClass = (ok) => ok ? "border-green-500 bg-green-50" : "border-blue-400 bg-white";

  const bookingComplete = isFilled(form.bookingNumber);
  const vesselComplete = isFilled(form.vesselCategory) && isFilled(form.vesselName);
  const datesInComplete = isFilled(form.checkInDate) && isFilled(form.checkInTime);
  const datesOutComplete = isFilled(form.checkOutDate) && isFilled(form.checkOutTime);
  const skipperNameComplete = isFilled(form.skipperFirstName) && isFilled(form.skipperLastName);
  /* ΕΤΑΙΡΙΚΟΣ ΝΑΥΛΩΤΗΣ (19/09/2026): ο έλεγχος διαβάζει ΚΑΙ flat ΚΑΙ nested —
     τα δύο σχήματα του booking_data ζουν μαζί (prod flat / online nested). */
  const chartererIsCompanyP = (() => {
    const truthy = (v) => v === true || v === 1 || ['1', 'true'].includes(String(v ?? '').toLowerCase());
    return truthy(form.chartererIsCompany)
        || truthy((form.chartererData || {}).isCompany)
        || ['company', 'legal'].includes(String(form.chartererType || '').toLowerCase());
  })();
  const addressComplete = isFilled(form.skipperAddress);
  const contactComplete = isFilled(form.skipperEmail) && isFilled(form.skipperPhone);

  const handleSaveDraft = () => {
    if (!currentBookingNumber || !form.bookingNumber) {
      alert(lang === 'el' ? 'Παρακαλώ εισάγετε αριθμό κράτησης!' : 'Please enter booking number!');
      return;
    }

    const dataToSave = { ...form, mode };
    saveBookingData(currentBookingNumber || form.bookingNumber, dataToSave);

    // 🔥 Mark as saved - clear unsaved changes flag
    setLastSavedForm(JSON.stringify(form));
    setHasUnsavedChanges(false);

    alert(lang === 'el' ? 'Το προσχέδιο αποθηκεύτηκε!' : 'Draft saved!');
  };

  const highlightElement = (ref) => {
    if (ref && ref.current) {
      ref.current.classList.add('validation-error');
      setTimeout(() => {
        if (ref.current) {
          ref.current.classList.remove('validation-error');
        }
      }, 3000);
    }
  };

  // Handle Enter key to move to next input field
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // Get all form inputs and selects
      const form = e.target.form;
      if (!form) return;

      const inputs = Array.from(form.querySelectorAll('input:not([type="hidden"]), select, textarea'));
      const currentIndex = inputs.indexOf(e.target);

      if (currentIndex < inputs.length - 1) {
        // Focus next input
        inputs[currentIndex + 1].focus();
      } else {
        // Last input - trigger save button
        const saveButton = form.querySelector('button[type="button"]');
        if (saveButton) saveButton.click();
      }
    }
  };

  const validateAndScroll = () => {
    // Employees can navigate without filling all fields
    if (isEmployee) return true;

    console.log("DEBUG VALIDATE:", JSON.stringify({bn: form.bookingNumber, fn: form.skipperFirstName, ln: form.skipperLastName, addr: form.skipperAddress, email: form.skipperEmail}));
    if (!form.bookingNumber) {
      bookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(bookingRef);
      alert(lang === 'el' ? 'Παρακαλώ συμπληρώστε τον αριθμό κράτησης!' : 'Please fill in the booking number!');
      return false;
    }

    // 🔥 NEW: Check for duplicate booking code
    if (bookingCodeError) {
      bookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(bookingRef);
      alert(bookingCodeError);
      return false;
    }

    if (!form.vesselCategory || !form.vesselName) {
      vesselRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(vesselRef);
      alert(lang === 'el' ? 'Παρακαλώ επιλέξτε σκάφος!' : 'Please select a vessel!');
      return false;
    }
    
    if (!form.checkInDate || !form.checkInTime) {
      checkInRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(checkInRef);
      alert(lang === 'el' ? 'Παρακαλώ συμπληρώστε ημερομηνία και ώρα επιβίβασης!' : 'Please fill in check-in date and time!');
      return false;
    }
    
    if (!form.checkOutDate || !form.checkOutTime) {
      checkOutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(checkOutRef);
      alert(lang === 'el' ? 'Παρακαλώ συμπληρώστε ημερομηνία και ώρα αποβίβασης!' : 'Please fill in check-out date and time!');
      return false;
    }
    
    // 🔥 CHECK: Check-out date must be >= Check-in date
    if (form.checkInDate && form.checkOutDate) {
      const checkInDate = new Date(form.checkInDate);
      const checkOutDate = new Date(form.checkOutDate);

      if (checkOutDate < checkInDate) {
        checkOutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        highlightElement(checkOutRef);
        alert(lang === 'el'
          ? '❌ Η ημερομηνία αποβίβασης δεν μπορεί να είναι πριν από την ημερομηνία επιβίβασης!'
          : '❌ Check-out date cannot be before check-in date!');
        return false;
      }
    }

    // 🔥 NEW: Check for double booking
    if (doubleBookingError) {
      checkInRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(checkInRef);
      alert(doubleBookingError);
      return false;
    }

    if (!form.skipperFirstName || !form.skipperLastName) {
      nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(nameRef);
      alert(lang === 'el' ? 'Παρακαλώ συμπληρώστε το όνομα και επώνυμο του κυβερνήτη!' : 'Please fill in skipper\'s name!');
      return false;
    }
    
    if (!form.skipperAddress) {
      addressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(addressRef);
      alert(lang === 'el' ? 'Παρακαλώ συμπληρώστε τη διεύθυνση του κυβερνήτη!' : 'Please fill in skipper\'s address!');
      return false;
    }
    
    if (!form.skipperEmail || !form.skipperPhone) {
      contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElement(contactRef);
      alert(lang === 'el' ? 'Παρακαλώ συμπληρώστε email και τηλέφωνο!' : 'Please fill in email and phone!');
      return false;
    }
    
    return true;
  };

  // 🟡 CHECK-IN PREVIEW — σήμερα < ημέρα check-in ΚΑΙ mode='in' → όλα δουλεύουν, τίποτα δεν σώζεται.
  const [isPreview, setIsPreview] = useState(false);
  useEffect(() => {
    setIsPreview(syncCheckinPreview(form.checkInDate, form.checkOutDate, mode));
  }, [form.checkInDate, form.checkOutDate, mode]);

  const handleNext = async () => {
    // Free navigation: validation removed - users can proceed without filling all fields

    // 🔥 DATE GUARD — check-in/out allowed ONLY on the charter day, FOR EVERYONE
    // (staff + customer). Uses the Athens-TZ charter window (single source of truth).
    // Monohimeros: isCheckOutWindow already true on the single day → check-out ok post-submit.
    // 🟡 PREVIEW: check-in ΠΡΙΝ την ημέρα του ναύλου → η ροή επιτρέπεται ως ΔΟΚΙΜΗ
    // (τα save είναι ήδη no-op μέσω previewMode). Το check-out ΔΕΝ επηρεάζεται.
    // 🔓 v6.478 — PENDING BYPASS (server-authoritative).
    // Ένα check-in που ΞΕΚΙΝΗΣΕ πρέπει να μπορεί να ΤΕΛΕΙΩΣΕΙ — ΚΑΙ αν πέρασε η
    // ημέρα του, για ΟΛΟΥΣ τους ρόλους. ΔΕΝ ανοίγει τρύπα για ΝΕΑ check-in εκτός
    // ημέρας: pending σημαίνει «υπάρχουν ΗΔΗ γραμμές στη βάση + κανένα submit»,
    // και το κρίνει ΜΟΝΟ ο SERVER (api/pending-checkins.php?action=check).
    let serverPending = false;
    const _bnGuard = currentBookingNumber || form.bookingNumber;
    if (_bnGuard) {
      try {
        const _pr = await fetch(
          'https://yachtmanagementsuite.com/api/pending-checkins.php?action=check'
          + '&booking_number=' + encodeURIComponent(_bnGuard)
          + '&mode=' + mode + '&t=' + Date.now()
        );
        if (_pr.ok) {
          const _pd = await _pr.json();
          serverPending = !!(_pd && _pd.success && _pd.pending);
        }
      } catch (e) {
        // fail-closed: αν δεν απαντήσει ο server, ισχύουν τα κανονικά date guards
        console.warn('pending check failed, date guards stay active:', e);
      }
    }
    if (serverPending) {
      console.log('🔓 PENDING (server): date guards bypassed for', _bnGuard, mode);
    }

    if (form.checkInDate && !serverPending && !(isPreview && mode === 'in')) {
      const wNext = getCharterWindow(form.checkInDate, form.checkOutDate);

      // Before the charter starts → always the check-in message (covers mode='out' too).
      if (wNext.isBeforeCheckIn) {
        const message = lang === 'el'
          ? `❌ Η κράτηση δεν έχει ξεκινήσει. Το Check-in γίνεται την ημέρα του ναύλου: ${formatDate(form.checkInDate)}`
          : `❌ The charter hasn't started yet. Check-in is on the charter day: ${formatDate(form.checkInDate)}`;
        alert(message);
        return;
      }

      // Check-in: ONLY on the check-in day (today === checkInDate).
      if (mode === 'in' && !wNext.isCheckInDay && currentEmployee?.role !== 'TECHNICAL') {
        const message = lang === 'el'
          ? `❌ Το Check-in επιτρέπεται μόνο την ημέρα του ναύλου!\n\nΗμερομηνία Check-in: ${formatDate(form.checkInDate)}\nΣημερινή ημερομηνία: ${formatDate(athensTodayYmd())}`
          : `❌ Check-in is only allowed on the charter day!\n\nCheck-in date: ${formatDate(form.checkInDate)}\nToday: ${formatDate(athensTodayYmd())}`;
        alert(message);
        return;
      }

      // Check-out: ONLY inside the 2-day window (endDate-1 OR endDate; single day if monohimeros).
      if (mode === 'out' && form.checkOutDate && !wNext.isCheckOutWindow && !isEarlyCheckoutBooking()) {
        const message = lang === 'el'
          ? `❌ Το Check-out είναι διαθέσιμο μόνο στις 2 τελευταίες ημέρες του ναύλου!\n\nΗμερομηνία Check-out: ${formatDate(form.checkOutDate)}\nΣημερινή ημερομηνία: ${formatDate(athensTodayYmd())}`
          : `❌ Check-out is only available on the last 2 days of the charter!\n\nCheck-out date: ${formatDate(form.checkOutDate)}\nToday: ${formatDate(athensTodayYmd())}`;
        alert(message);
        return; // Don't navigate
      }
    }
    
    const bookingNum = currentBookingNumber || form.bookingNumber;
    const dataToSave = { ...form, mode };
    saveBookingData(bookingNum, dataToSave);
    
    // 🔥 CRITICAL: Set currentBookingNumber if it's a new booking
    if (!currentBookingNumber && form.bookingNumber) {
      setCurrentBookingNumber(form.bookingNumber);
    }
    
    // 🔥 UPDATE CONTEXT with booking number and mode
    if (updateData) {
      updateData({ 
        bookingNumber: bookingNum,
        mode: mode 
      });
    }
    
    navigate('/page2');
  };

  const generatePDF = () => {
    if (!validateAndScroll()) {
      return;
    }

    try {
      const bookingData = {
        bookingNumber: form.bookingNumber,
        vesselName: `${form.vesselCategory} - ${form.vesselName}`,
        selectedVessel: form.vesselName,
        // Charterer fields
        chartererFirstName: form.chartererFirstName,
        chartererLastName: form.chartererLastName,
        chartererAddress: form.chartererAddress,
        chartererEmail: form.chartererEmail,
        chartererPhone: form.chartererPhone,
        chartererDob: form.chartererDob || '', chartererGender: form.chartererGender || '', chartererNationality: form.chartererNationality || '',
        sameAsSkipper: form.sameAsSkipper,
        // Skipper fields
        skipperFirstName: form.skipperFirstName,
        skipperLastName: form.skipperLastName,
        skipperEmail: form.skipperEmail,
        skipperPhone: `${form.phoneCountryCode} ${form.skipperPhone}`,
        skipperAddress: form.skipperAddress,
        skipperDob: form.skipperDob || '', skipperGender: form.skipperGender || '', skipperNationality: form.skipperNationality || '',
        checkInDate: form.checkInDate,
        checkInTime: form.checkInTime,
        checkOutDate: form.checkOutDate,
        checkOutTime: form.checkOutTime,
      };

      const pdf = generateLuxuryPDF(
        bookingData,
        mode,
        {},
        lang,
        { isPage1: true }
      );

      const fileName = `Page1_Booking_${form.bookingNumber}_${Date.now()}.pdf`;
      pdf.save(fileName);

      console.log('✅ PDF generated successfully!');
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      alert('Error generating PDF. Check console for details.');
    }
  };

  const handleCountryCodeChange = (newCode) => {
    setForm({ ...form, phoneCountryCode: newCode });
    if (newCode === "+30") setLang("el");
    else if (newCode === "+44" || newCode === "+1") setLang("en");
  };

  const SuggestionItem = ({ suggestion, fieldName }) => (
    <div 
      className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b"
      onMouseDown={(e) => {
        e.preventDefault();
        setForm(prev => ({ ...prev, [fieldName]: suggestion }));
        setShowSuggestions({
          bookingNumber: false,
          firstName: false,
          lastName: false,
          address: false,
          email: false,
          phone: false,
        });
      }}
    >
      {suggestion}
    </div>
  );

  // 🔥 UPDATED: Use global bookings from App.tsx context (auto-refreshes every 3 minutes)
  const allBookings = globalBookings || [];
  const isLoadingBookings = globalIsRefreshing || false;

  // 🔥 Listen for global refresh events (triggered by App.tsx auto-refresh)
  useEffect(() => {
    const handleGlobalRefresh = (e: CustomEvent) => {
      console.log('🔄 Page 1: Received global bookings refresh', e.detail?.bookings?.length, 'bookings');
    };

    window.addEventListener('globalBookingsRefreshed', handleGlobalRefresh as EventListener);
    return () => window.removeEventListener('globalBookingsRefreshed', handleGlobalRefresh as EventListener);
  }, []);

  // Refresh from API when counter changes
  useEffect(() => {
    if (refreshCounter > 0 && refreshBookings) {
      console.log('🔄 Page 1: Refreshing from API');
      refreshBookings();
    }
  }, [refreshCounter, refreshBookings]);

  // 🔥 Compute today's bookings: check-in day + check-out day + day BEFORE check-out
  const todayBookings = useMemo(() => {
    // 🔥 Europe/Athens (ΟΧΙ device time) — το "today"/"tomorrow" γυρίζουν στα μεσάνυχτα Αθήνας.
    const _now = new Date();
    const today = athensTodayYmd(_now);
    const _tom = new Date(_now);
    _tom.setDate(_tom.getDate() + 1);
    const tomorrow = athensTodayYmd(_tom);
    // 🔥 ΚΑΝΟΝΑΣ: σύγκριση ΗΜΕΡΟΜΗΝΙΑΣ, ΟΧΙ ώρας. Ο ναύλος μένει στη λίστα check-out
    // μέχρι το ΤΕΛΟΣ της ημέρας λήξης (μεσάνυχτα Αθήνας). Το παλιό ωριαίο cutoff
    // (10:00) τον εξαφάνιζε ενώ το check-out δεν είχε γίνει — ναύλος 2800, λήξη
    // 28/08 09:00: στις 09:01 έφευγε από τη λίστα με τη δουλειά ανοιχτή.
    return allBookings.filter(booking => {
      const checkInDate = String(booking?.startDate || "").slice(0, 10);
      const checkOutDate = String(booking?.endDate || "").slice(0, 10);
      if (checkInDate === today) return true;                    // check-in day → πάντα
      if (checkOutDate === today) return true;                    // ημέρα λήξης → ΟΛΗ τη μέρα
      if (checkOutDate === tomorrow) return true;                // day BEFORE check-out → πάντα
      if (booking.early_checkout) {
        if (checkOutDate === today)    return true;                 // ληξη σημερα -> ΟΛΗ τη μέρα
        if (checkOutDate === tomorrow) return true;                // ληξη αυριο -> δειξε
        return checkOutDate > today;                               // περασμενη ληξη -> φυγε
      }
      return false;
    });
  }, [allBookings]);

  // 🔥 NEW: Determine which bookings to show (ensure it's always an array)
  const displayedBookings = Array.isArray(showAllBookings ? allBookings : todayBookings)
    ? (showAllBookings ? allBookings : todayBookings)
    : [];

  // ═══ EARLY CHECK-OUT popup: ΚΟΙΝΗ ταξινόμηση/χρώματα/αναζήτηση (earlyCheckout.ts) ═══
  //  Σε εξέλιξη (boat out τώρα) → τρέχουσα εβδομάδα → προηγούμενοι φθίνουσα → μελλοντικοί.
  //  statusOf από το action=states (ίδιος μηχανισμός με την pending list).
  const [ecStates, setEcStates] = useState<any>({});
  const [ecSearch, setEcSearch] = useState('');
  useEffect(() => {
    if (!showBookingSelector) return;
    fetch('/api/pending-checkins.php?action=states&t=' + Date.now())
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.states) setEcStates(d.states); })
      .catch(() => {});
  }, [showBookingSelector]);
  const ecNorm = (v: any) => String(v || '').replace(/\D/g, '');
  const ecStatusOf = (b: any) => {
    const s = (ecStates as any)[ecNorm(b.booking_number || b.bookingNumber || b.code || b.charterCode)] || {};
    return { checkInDone: !!(s.in && s.in.submitted), checkOutDone: !!(s.out && s.out.submitted) };
  };
  const ecRows = orderEarlyCheckout(
    (displayedBookings || []).filter((b: any) => ecMatches(b, ecSearch)),
    ecStatusOf, { mode: 'out' },
  );
  // Δικαίωμα Early Check-out: TECHNICAL/BASE_MANAGER είναι στη βάση με τον πελάτη — όχι μόνο ADMIN.
  const canEarlyCheckout = ['ADMIN', 'TECHNICAL', 'BASE_MANAGER'].includes(currentEmployee?.role || '');

  return (
    <>
      <style>{`
        .validation-error {
          animation: errorPulse 3s;
          border-color: #ef4444 !important;
          background-color: #fee2e2 !important;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.3) !important;
        }
        @keyframes errorPulse {
          0%, 100% { 
            box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.3);
          }
          50% { 
            box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.2);
          }
        }
      `}</style>
      
      <div className="min-h-screen p-6" style={{ background: brand.pageBg }}>
        <div className="max-w-3xl mx-auto bg-sky-100 shadow rounded-2xl p-6 border-2" style={{ borderColor: brand.black }}>

          <div className="bg-white border-2 border-blue-500 rounded-xl p-4 mb-6">
            <div className="text-center mb-4">
              <div className="inline-block bg-blue-500 text-white px-6 py-2 rounded-lg font-bold text-xl mb-3">
                TAILWIND YACHTING
              </div>
              
              <div className="text-xs text-gray-600 mb-2">
                📍 Leukosias 37, Alimos | 📧 info@tailwindyachting.com | 📞 +30 697 819 6009
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm border-t border-blue-200 pt-3">
              <div>
                <span className="font-semibold text-gray-700">📋 {t.bookingNumber.replace('1. ', '')}:</span>{" "}
                <span className="text-blue-700 font-medium">
                  {form.bookingNumber || <em className="text-gray-400">{t.notSet}</em>}
                </span>
              </div>
              
              <div>
                <span className="font-semibold text-gray-700">🚤 {t.vessel}:</span>{" "}
                <span className="text-blue-700 font-medium">
                  {form.vesselName 
                    ? `${form.vesselCategory} - ${form.vesselName}` 
                    : <em className="text-gray-400">{t.notSelected}</em>
                  }
                </span>
              </div>
              
              <div>
                <span className="font-semibold text-gray-700">📅 Check-in:</span>{" "}
                <span className="text-blue-700 font-medium">
                  {form.checkInDate && form.checkInTime
                    ? `${formatDate(form.checkInDate)} ${form.checkInTime}`
                    : <em className="text-gray-400">{t.notSet}</em>
                  }
                </span>
              </div>
              
              <div>
                <span className="font-semibold text-gray-700">🏁 Check-out:</span>{" "}
                <span className="text-blue-700 font-medium">
                  {form.checkOutDate && form.checkOutTime
                    ? `${formatDate(form.checkOutDate)} ${form.checkOutTime}`
                    : <em className="text-gray-400">{t.notSet}</em>
                  }
                </span>
              </div>
              
              <div>
                <span className="font-semibold text-gray-700">{t.mode}</span>{" "}
                <span className={mode === 'in' ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                  {mode === 'in' ? `✅ ${t.checkin}` : `🚪 ${t.checkout}`}
                </span>
              </div>
              
              <div>
                <span className="font-semibold text-gray-700">{t.status}</span>{" "}
                <span className={
                  networkStatus === 'online' ? 'text-green-600' : 'text-orange-600'
                }>
                  {networkStatus === 'online' ? `✅ ${t.synced}` : `📴 ${t.offline}`}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6 space-y-4">
            <div className="bg-white border-2 border-blue-400 rounded-xl p-4">
              <label className="block font-semibold mb-2 text-gray-700">
                {t.selectBooking}
              </label>
              
              <button
                onClick={() => setShowBookingSelector(true)}
                className="w-full px-4 py-3 rounded-lg border-2 border-blue-400 bg-white text-left hover:bg-blue-50 transition-all duration-200 flex items-center justify-between"
              >
                <span className="font-medium text-blue-700">
                  {currentBookingNumber 
                    ? `${currentBookingNumber} - ${form.vesselName || 'No vessel'} - ${formatDate(form.checkInDate) || 'No date'}`
                    : t.noBookings
                  }
                </span>
                <span className="text-blue-500">▼</span>
              </button>
              
            </div>

            <div className="flex items-center justify-center">
              <button
                onClick={handleToggleMode}
                className={`px-6 py-3 rounded-lg font-bold text-white transition-all duration-300 transform hover:scale-105 shadow-lg ${
                  mode === 'in' 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {mode === 'in' ? '✅ CHECK-IN MODE' : '🚪 CHECK-OUT MODE'}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setLang("en")} 
                className={`px-3 py-1 rounded border ${lang === "en" ? "bg-blue-500 text-white" : "bg-white text-blue-500"}`}>
                🇬🇧 EN
              </button>
              <button type="button" onClick={() => setLang("el")} 
                className={`px-3 py-1 rounded border ${lang === "el" ? "bg-blue-500 text-white" : "bg-white text-blue-500"}`}>
                🇬🇷 GR
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              {/* 🔒 Identity chip removed — auth/identity UI lives only on Page 5 (signature). */}
              {/* isEmployee / currentEmployee / handleEmployeeLogout state retained as dead code per spec. */}
            </div>
          </div>

          {/* 🔒 "Clear All Data" red button removed — admin tool no longer surfaced on Page 1. */}
          {/* handleClearAllData / canClearData permission retained as dead code per spec. */}

          <div className="space-y-6 text-lg" style={{ color: brand.black }}>
            
            <div ref={bookingRef} className="border-2 rounded-xl p-4 relative transition-all duration-300"
              style={{
                borderColor: bookingComplete ? brand.successBorder : brand.blue,
                background: bookingComplete ? brand.successBg : 'transparent'
              }}>
              <label className="block font-semibold cursor-pointer">
                {t.bookingNumber} <span className="text-red-500">*</span>
                <span className="ml-2 text-sm bg-gray-500 text-white px-2 py-1 rounded">
                  {lang === 'el' ? '🔒 Επιλέξτε από λίστα' : '🔒 Select from list'}
                </span>
              </label>
              <input
                ref={bookingInputRef}
                aria-label={stripNumber(t.bookingNumber)}
                name="bookingNumber"
                value={form.bookingNumber}
                readOnly
                disabled
                className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.bookingNumber)} bg-gray-100 cursor-not-allowed`}
                placeholder={lang === 'el' ? 'Επιλέξτε κράτηση από τη λίστα παραπάνω' : 'Select a booking from the list above'}
              />
            </div>

            <div ref={checkInRef} className="border-2 rounded-xl p-4 transition-all duration-300" 
              style={{ 
                borderColor: datesInComplete ? brand.successBorder : brand.blue, 
                background: datesInComplete ? brand.successBg : 'transparent' 
              }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold cursor-pointer" onClick={()=>speakLabel(t.dateIn)}>
                    {t.dateIn} <span className="text-red-500">*</span>
                    {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
                  </label>
                  <input
                    type="date"
                    name="checkInDate"
                    value={form.checkInDate}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    min={new Date().toISOString().split('T')[0]}
                    onFocus={()=>speakLabel(t.dateIn)}
                    disabled={true}
                    className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.checkInDate)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <label className="block font-semibold cursor-pointer" onClick={()=>speakLabel(t.timeIn)}>
                    {t.timeIn} <span className="text-red-500">*</span>
                    {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
                  </label>
                  <input
                    type="time"
                    name="checkInTime"
                    value={form.checkInTime}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    onFocus={()=>speakLabel(t.timeIn)}
                    disabled={true}
                    className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.checkInTime)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>
            </div>

            <div ref={checkOutRef} className="border-2 rounded-xl p-4 transition-all duration-300" 
              style={{ 
                borderColor: datesOutComplete ? brand.successBorder : brand.blue, 
                background: datesOutComplete ? brand.successBg : 'transparent' 
              }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold cursor-pointer" onClick={()=>speakLabel(t.dateOut)}>
                    {t.dateOut} <span className="text-red-500">*</span>
                    {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
                  </label>
                  <input
                    type="date"
                    name="checkOutDate"
                    value={form.checkOutDate}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    min={form.checkInDate || new Date().toISOString().split('T')[0]}
                    onFocus={()=>speakLabel(t.dateOut)}
                    disabled={true}
                    className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.checkOutDate)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <label className="block font-semibold cursor-pointer" onClick={()=>speakLabel(t.timeOut)}>
                    {t.timeOut} <span className="text-red-500">*</span>
                    {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
                  </label>
                  <input
                    type="time"
                    name="checkOutTime"
                    value={form.checkOutTime}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    onFocus={()=>speakLabel(t.timeOut)}
                    disabled={true}
                    className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.checkOutTime)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>

              {/* 🔥 NEW: DOUBLE BOOKING ERROR */}
              {doubleBookingError && (
                <div className="mt-3 p-3 bg-red-100 border-2 border-red-500 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">❌</span>
                    <p className="font-bold text-red-800">{doubleBookingError}</p>
                  </div>
                </div>
              )}
            </div>

            {/* CHARTERER INFORMATION Section
                ΕΤΑΙΡΙΚΟΣ ΝΑΥΛΩΤΗΣ (19/09/2026): read-only όπως πάντα, αλλά με
                ΕΠΩΝΥΜΙΑ αντί για Όνομα/Επώνυμο και ΧΩΡΙΣ γέννηση/φύλο/
                εθνικότητα — μια εταιρεία δεν έχει τίποτα από αυτά. */}
            <div className="border-2 rounded-xl p-4 transition-all duration-300"
              style={{
                borderColor: (form.chartererFirstName && form.chartererLastName) ? '#10b981' : '#22c55e',
                background: (form.chartererFirstName && form.chartererLastName) ? '#f0fdf4' : 'transparent',
                borderWidth: '2px'
              }}>
              <h3 className="text-lg font-bold text-green-600 mb-3">
                {lang === 'el' ? 'CHARTERER INFORMATION' : 'CHARTERER INFORMATION'}
              </h3>
              {chartererIsCompanyP ? (
              <div data-yms-charterer-company>
                <label className="block font-semibold text-green-700">
                  {lang === 'el' ? 'Επωνυμία' : 'Company name'}
                </label>
                <input
                  name="chartererCompanyName"
                  value={form.chartererCompanyName || form.chartererLastName || ''}
                  disabled={true}
                  className="w-full border-2 border-green-400 rounded p-2 mt-2 bg-gray-100 cursor-not-allowed"
                  placeholder={lang === 'el' ? 'Επωνυμία' : 'Company name'}
                />
              </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-green-700">
                    {lang === 'el' ? 'Όνομα' : 'First Name'}
                  </label>
                  <input
                    name="chartererFirstName"
                    value={form.chartererFirstName}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    disabled={true}
                    className={`w-full border-2 border-green-400 rounded p-2 mt-2 transition-all duration-300 focus:border-green-600 focus:outline-none ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder={lang === 'el' ? 'Όνομα' : 'First Name'}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-green-700">
                    {lang === 'el' ? 'Επώνυμο' : 'Last Name'}
                  </label>
                  <input
                    name="chartererLastName"
                    value={form.chartererLastName}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    disabled={true}
                    className={`w-full border-2 border-green-400 rounded p-2 mt-2 transition-all duration-300 focus:border-green-600 focus:outline-none ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder={lang === 'el' ? 'Επώνυμο' : 'Last Name'}
                  />
                </div>
              </div>
              )}
              <div className="mt-3">
                <label className="block font-semibold text-green-700">
                  {lang === 'el' ? 'Διεύθυνση' : 'Address'}
                </label>
                <input
                  name="chartererAddress"
                  value={form.chartererAddress}
                  onChange={handleChange}
                  onKeyDown={handleFormKeyDown}
                  disabled={true}
                  className={`w-full border-2 border-green-400 rounded p-2 mt-2 transition-all duration-300 focus:border-green-600 focus:outline-none ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder={lang === 'el' ? 'Διεύθυνση' : 'Address'}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block font-semibold text-green-700">Email</label>
                  <input
                    type="email"
                    name="chartererEmail"
                    value={form.chartererEmail}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    disabled={true}
                    className={`w-full border-2 border-green-400 rounded p-2 mt-2 transition-all duration-300 focus:border-green-600 focus:outline-none ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-green-700">
                    {lang === 'el' ? 'Τηλέφωνο' : 'Phone'}
                  </label>
                  <input
                    type="tel"
                    name="chartererPhone"
                    value={form.chartererPhone}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    disabled={true}
                    className={`w-full border-2 border-green-400 rounded p-2 mt-2 transition-all duration-300 focus:border-green-600 focus:outline-none ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="+30 69..."
                  />
                </div>
              </div>
              {chartererIsCompanyP ? (
                <div className="mt-3" data-yms-charterer-company-extra>
                  <label className="block font-semibold text-green-700">{lang === 'el' ? 'Χώρα' : 'Country'}</label>
                  <input type="text" name="chartererCountry" value={form.chartererCountry ?? ''} disabled={true}
                         className="w-full border-2 border-green-400 rounded p-2 mt-2 bg-gray-100 cursor-not-allowed"
                         placeholder={lang === 'el' ? 'Χώρα' : 'Country'} />
                </div>
              ) : (
              <div data-yms-charterer-personal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block font-semibold text-green-700">{lang === 'el' ? 'Ημ. Γέννησης' : 'Date of Birth'}</label>
                  <input type="date" name="chartererDob" value={form.chartererDob ?? ''} onChange={handleChange} disabled={true} className="w-full border-2 border-green-400 rounded p-2 mt-2 focus:border-green-600 focus:outline-none" />
                </div>
                <div>
                  <label className="block font-semibold text-green-700">{lang === 'el' ? 'Φύλο' : 'Gender'}</label>
                  <select name="chartererGender" value={form.chartererGender ?? ''} onChange={handleChange} disabled={true} className="w-full border-2 border-green-400 rounded p-2 mt-2 focus:border-green-600 focus:outline-none">
                    <option value="">Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label className="block font-semibold text-green-700">{lang === 'el' ? 'Εθνικότητα' : 'Nationality'}</label>
                <input type="text" name="chartererNationality" value={form.chartererNationality ?? ''} onChange={handleChange} disabled={true} className="w-full border-2 border-green-400 rounded p-2 mt-2 focus:border-green-600 focus:outline-none" placeholder={lang === 'el' ? 'Εθνικότητα' : 'Nationality'} />
              </div>
              </div>
              )}

            </div>

            {/* SKIPPER INFORMATION Section */}
            <div ref={nameRef} className="border-2 rounded-xl p-4 transition-all duration-300"
              style={{
                borderColor: skipperNameComplete ? brand.successBorder : brand.blue,
                background: skipperNameComplete ? brand.successBg : 'transparent'
              }}>
              <h3 className="text-lg font-bold mb-3" style={{ color: brand.blue }}>
                {lang === 'el' ? 'SKIPPER INFORMATION' : 'SKIPPER INFORMATION'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block font-semibold cursor-pointer" onClick={()=>speakLabel(t.firstName)}>
                    {t.firstName} <span className="text-red-500">*</span>
                    {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
                  </label>
                  <input
                    name="skipperFirstName"
                    value={form.skipperFirstName}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    disabled={true}
                    onFocus={() => {
                      speakLabel(t.firstName);
                      if (mode === 'in') setShowSuggestions({ ...showSuggestions, firstName: true });
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSuggestions({ ...showSuggestions, firstName: false }), 200);
                    }}
                    className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.skipperFirstName)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder={lang==='el' ? 'Όνομα' : 'First Name'}
                  />
                  {showSuggestions.firstName && mode === 'in' && (
                    <div className="absolute z-10 w-full bg-white border border-blue-400 rounded mt-1 max-h-40 overflow-y-auto shadow-lg">
                      <div className="text-xs font-semibold px-3 py-2 bg-gray-100">{t.recentEntries}</div>
                      {getFilteredSuggestions('skipperFirstName', form.skipperFirstName).map((s, i) => (
                        <SuggestionItem key={i} suggestion={s} fieldName="skipperFirstName" />
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <label className="block font-semibold cursor-pointer" onClick={()=>speakLabel(t.lastName)}>
                    {t.lastName} <span className="text-red-500">*</span>
                    {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
                  </label>
                  <input
                    name="skipperLastName"
                    value={form.skipperLastName}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    disabled={true}
                    onFocus={() => {
                      speakLabel(t.lastName);
                      if (mode === 'in') setShowSuggestions({ ...showSuggestions, lastName: true });
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSuggestions({ ...showSuggestions, lastName: false }), 200);
                    }}
                    className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.skipperLastName)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder={lang==='el' ? 'Επώνυμο' : 'Last Name'}
                  />
                  {showSuggestions.lastName && mode === 'in' && (
                    <div className="absolute z-10 w-full bg-white border border-blue-400 rounded mt-1 max-h-40 overflow-y-auto shadow-lg">
                      <div className="text-xs font-semibold px-3 py-2 bg-gray-100">{t.recentEntries}</div>
                      {getFilteredSuggestions('skipperLastName', form.skipperLastName).map((s, i) => (
                        <SuggestionItem key={i} suggestion={s} fieldName="skipperLastName" />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div ref={addressRef} className="border-2 rounded-xl p-4 relative transition-all duration-300" 
              style={{ 
                borderColor: addressComplete ? brand.successBorder : brand.blue, 
                background: addressComplete ? brand.successBg : 'transparent' 
              }}>
              <label className="block font-semibold cursor-pointer" onClick={()=>speakLabel(t.address)}>
                {t.address} <span className="text-red-500">*</span>
                {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
              </label>
              <input
                name="skipperAddress"
                value={form.skipperAddress}
                onChange={handleChange}
                onKeyDown={handleFormKeyDown}
                disabled={true}
                onFocus={() => {
                  speakLabel(t.address);
                  if (mode === 'in') setShowSuggestions({ ...showSuggestions, address: true });
                }}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions({ ...showSuggestions, address: false }), 200);
                }}
                className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.skipperAddress)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder={lang==='el' ? 'Διεύθυνση' : 'Address'}
              />
              {showSuggestions.address && mode === 'in' && (
                <div className="absolute z-10 w-full bg-white border border-blue-400 rounded mt-1 max-h-40 overflow-y-auto shadow-lg">
                  <div className="text-xs font-semibold px-3 py-2 bg-gray-100">{t.recentEntries}</div>
                  {getFilteredSuggestions('skipperAddress', form.skipperAddress).map((s, i) => (
                    <SuggestionItem key={i} suggestion={s} fieldName="skipperAddress" />
                  ))}
                </div>
              )}
            </div>

            <div ref={contactRef} className="border-2 rounded-xl p-4 transition-all duration-300" 
              style={{ 
                borderColor: contactComplete ? brand.successBorder : brand.blue, 
                background: contactComplete ? brand.successBg : 'transparent' 
              }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block font-semibold cursor-pointer" onClick={()=>speakLabel(t.email)}>
                    {t.email} <span className="text-red-500">*</span>
                    {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
                  </label>
                  <input
                    type="email"
                    name="skipperEmail"
                    value={form.skipperEmail}
                    onChange={handleChange}
                    onKeyDown={handleFormKeyDown}
                    disabled={true}
                    onFocus={() => {
                      speakLabel(t.email);
                      if (mode === 'in') setShowSuggestions({ ...showSuggestions, email: true });
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSuggestions({ ...showSuggestions, email: false }), 200);
                    }}
                    className={`w-full border rounded p-2 mt-2 transition-all duration-300 ${filledClass(!!form.skipperEmail)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="email@example.com"
                  />
                  {showSuggestions.email && mode === 'in' && (
                    <div className="absolute z-10 w-full bg-white border border-blue-400 rounded mt-1 max-h-40 overflow-y-auto shadow-lg">
                      <div className="text-xs font-semibold px-3 py-2 bg-gray-100">{t.recentEntries}</div>
                      {getFilteredSuggestions('skipperEmail', form.skipperEmail).map((s, i) => (
                        <SuggestionItem key={i} suggestion={s} fieldName="skipperEmail" />
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <label className="block font-semibold cursor-pointer mb-2" onClick={()=>speakLabel(t.phone)}>
                    {t.phone} <span className="text-red-500">*</span>
                    {mode === 'out' && <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">🔒</span>}
                  </label>
                  <div className="flex gap-2 min-w-0">
                    <select
                      value={form.phoneCountryCode}
                      onChange={(e) => handleCountryCodeChange(e.target.value)}
                      onKeyDown={handleFormKeyDown}
                      disabled={true}
                      className={`border border-blue-400 rounded p-2 bg-white shrink-0 ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      style={{ width: "100px", minWidth: "100px" }}
                    >
                      {COUNTRY_CODES.map(({ code, country, flag }) => (
                        <option key={`${country}-${code}`} value={code}>
                          {flag} {code}
                        </option>
                      ))}
                    </select>
                    <div className="flex-1 relative">
                      <input
                        type="tel"
                        name="skipperPhone"
                        value={form.skipperPhone}
                        onChange={handleChange}
                        onKeyDown={handleFormKeyDown}
                        disabled={true}
                        onFocus={() => {
                          speakLabel(t.phone);
                          if (mode === 'in') setShowSuggestions({ ...showSuggestions, phone: true });
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowSuggestions({ ...showSuggestions, phone: false }), 200);
                        }}
                        className={`w-full border rounded p-2 transition-all duration-300 ${filledClass(!!form.skipperPhone)} ${mode === 'out' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        placeholder={lang==='el' ? '69XXXXXXXX' : 'Phone number'}
                      />
                      {showSuggestions.phone && mode === 'in' && (
                        <div className="absolute z-10 w-full bg-white border border-blue-400 rounded mt-1 max-h-40 overflow-y-auto shadow-lg">
                          <div className="text-xs font-semibold px-3 py-2 bg-gray-100">{t.recentEntries}</div>
                          {getFilteredSuggestions('skipperPhone', form.skipperPhone).map((s, i) => (
                            <SuggestionItem key={i} suggestion={s} fieldName="skipperPhone" />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-2 rounded-xl p-4 transition-all duration-300" style={{ borderColor: brand.blue }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold" style={{ color: brand.blue }}>{lang === 'el' ? 'Ημ. Γέννησης (Skipper)' : 'Date of Birth (Skipper)'}</label>
                  <input type="date" name="skipperDob" value={form.skipperDob ?? ''} onChange={handleChange} disabled={true} className="w-full border rounded p-2 mt-2" />
                </div>
                <div>
                  <label className="block font-semibold" style={{ color: brand.blue }}>{lang === 'el' ? 'Φύλο (Skipper)' : 'Gender (Skipper)'}</label>
                  <select name="skipperGender" value={form.skipperGender ?? ''} onChange={handleChange} disabled={true} className="w-full border rounded p-2 mt-2">
                    <option value="">Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label className="block font-semibold" style={{ color: brand.blue }}>{lang === 'el' ? 'Εθνικότητα (Skipper)' : 'Nationality (Skipper)'}</label>
                <input type="text" name="skipperNationality" value={form.skipperNationality ?? ''} onChange={handleChange} disabled={true} className="w-full border rounded p-2 mt-2" placeholder={lang === 'el' ? 'Εθνικότητα' : 'Nationality'} />
              </div>
            </div>

            <div className="grid grid-cols-2 md:flex md:items-center md:justify-between gap-2 md:gap-3">
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="px-3 py-3 rounded text-sm font-medium bg-gray-600 text-white hover:bg-gray-700 flex items-center justify-center gap-1 min-h-[44px] col-span-2 md:col-span-1 md:order-first">
                ← 🏠 {lang === 'el' ? 'Αρχική' : 'Home'}
              </button>
              <div className="grid grid-cols-2 md:flex gap-2 md:gap-3 col-span-2 md:col-span-1">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="px-3 py-3 rounded text-sm font-medium bg-gray-600 text-white hover:bg-gray-700 min-h-[44px]">
                  {t.saveDraft}
                </button>
                <button
                  type="button"
                  onClick={generatePDF}
                  className="px-3 py-3 rounded text-sm font-medium bg-red-500 text-white hover:bg-red-600 min-h-[44px]">
                  📄 {t.pdf}
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-3 py-3 rounded text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 min-h-[44px]">
                  {t.next} →
                </button>
              </div>
            </div>
          </div>
        </div>

        {showBookingSelector && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">{t.selectBooking}</h3>
                <button className="text-2xl hover:text-red-500" onClick={()=>{setShowBookingSelector(false); setShowAllBookings(false); setEcSearch('');}}>×</button>
              </div>

              {/* 🔎 ΑΝΑΖΗΤΗΣΗ (αριθμός / σκάφος / όνομα) — καθώς γράφεις */}
              <input
                value={ecSearch}
                onChange={(e) => setEcSearch(e.target.value)}
                placeholder={lang === 'el' ? 'Αναζήτηση: αριθμός, σκάφος, όνομα…' : 'Search: number, vessel, name…'}
                className="w-full mb-4 px-3 py-2 border-2 border-gray-300 rounded-lg text-sm min-h-[44px]"
              />

              {/* 🔥 TODAY'S BOOKINGS HEADER */}
              {!showAllBookings && todayBookings.length > 0 && (
                <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-400 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-blue-700 text-lg">
                        📅 {t.todayBookings}
                      </div>
                      <div className="text-sm text-blue-600">
                        🚤 {todayBookings.length} {t.vesselsToday}
                      </div>
                    </div>
                    <div className="text-3xl">
                      {new Date().toLocaleDateString(lang === 'el' ? 'el-GR' : 'en-GB')}
                    </div>
                  </div>
                </div>
              )}

              {ecRows.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg mb-4">
                    {ecSearch.trim()
                      ? (lang === 'el' ? 'Κανένας ναύλος με αυτά τα κριτήρια.' : 'No charters match.')
                      : (showAllBookings ? t.noBookings : (lang === 'el' ? 'Δεν υπάρχουν κρατήσεις σήμερα' : 'No bookings today'))}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {ecRows.map(({ b: booking, bucket }) => {
                    // 🔥 Europe/Athens YMD + 10:00 cutoff — ίδια λογική με todayBookings filter.
                    const _n = new Date();
                    const _today = athensTodayYmd(_n);
                    const _tm = new Date(_n);
                    _tm.setDate(_tm.getDate() + 1);
                    const _tomorrow = athensTodayYmd(_tm);
                    const checkInDate = String(booking.startDate || booking.checkInDate || '').slice(0, 10);
                    const checkOutDate = String(booking.endDate || booking.checkOutDate || '').slice(0, 10);
                    const isCheckInToday = checkInDate === _today;
                    // check-out badge: ΟΛΗ την ημέρα λήξης, ή παραμονή check-out (ίδιος
                    // κανόνας με το φίλτρο todayBookings — ημερομηνία, όχι ώρα).
                    const isCheckOutToday = checkOutDate === _today || checkOutDate === _tomorrow;

                    // 🔥 FIX: Use consistent booking code with fallbacks
                    const bookingCodeValue = booking.bookingCode || booking.code || booking.bookingNumber || booking.charterCode;

                    return (
                      <div
                        key={bookingCodeValue}
                        onClick={() => handleSelectBooking(bookingCodeValue)}
                        style={{ background: ecRowBg(bucket) }}
                        className={`p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-all ${
                          bookingCodeValue === currentBookingNumber
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold text-lg text-blue-700 flex items-center gap-2">
                              📋 {booking.bookingNumber || booking.code || booking.charterCode}
                              {isCheckInToday && (
                                <span className="text-xs px-2 py-1 bg-green-500 text-white rounded-full font-medium">
                                  {lang === 'el' ? 'ΕΠΙΒΙΒΑΣΗ' : 'CHECK-IN'}
                                </span>
                              )}
                              {isCheckOutToday && (
                                <span className="text-xs px-2 py-1 bg-orange-500 text-white rounded-full font-medium">
                                  {lang === 'el' ? 'ΑΠΟΒΙΒΑΣΗ' : 'CHECK-OUT'}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              🚤 {booking.vesselCategory} - {booking.vesselName || booking.boatName || 'Vessel'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              📅 {formatDate(booking.startDate || booking.checkInDate)} {booking.checkInTime} → {formatDate(booking.endDate || booking.checkOutDate)} {booking.checkOutTime}
                            </div>
                            {canEarlyCheckout && computeAutoMode(booking.startDate || booking.checkInDate, booking.endDate || booking.checkOutDate) !== 'out' && !booking.early_checkout && (
                              <button
                                onClick={(e) => handleEarlyCheckout(e, bookingCodeValue)}
                                className="mt-2 text-xs px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                              >
                                {lang === 'el' ? 'Προωρο Check-out' : 'Early Check-out'}
                              </button>
                            )}
                            {booking.early_checkout && (
                              <span className="mt-2 mr-2 inline-block text-xs px-2 py-1 bg-orange-500 text-white rounded-full font-medium">
                                {lang === 'el' ? 'ΑΠΟΒΙΒΑΣΗ' : 'CHECK-OUT'}
                              </span>
                            )}
                            {canEarlyCheckout && booking.early_checkout && (
                              <button
                                onClick={(e) => handleEarlyCheckout(e, bookingCodeValue)}
                                className="mt-2 text-xs px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                              >
                                {lang === 'el' ? 'Κλειδωμα' : 'Lock'}
                              </button>
                            )}
                            {/* 📧 RESEND CHECK-IN EMAIL (staff-only) — with visual feedback */}
                            {currentEmployee && (() => {
                              const _rs = resendStatus[bookingCodeValue];
                              const _cls = _rs === 'sent' ? 'bg-green-600 hover:bg-green-700'
                                : _rs === 'error' ? 'bg-red-600 hover:bg-red-700'
                                : _rs === 'sending' ? 'bg-gray-400'
                                : 'bg-sky-600 hover:bg-sky-700';
                              const _label = _rs === 'sending' ? (lang === 'el' ? '⏳ Αποστολή...' : '⏳ Sending...')
                                : _rs === 'sent' ? (lang === 'el' ? '✅ Στάλθηκε' : '✅ Sent')
                                : _rs === 'error' ? (lang === 'el' ? '❌ Απέτυχε' : '❌ Failed')
                                : (lang === 'el' ? '📧 Ξαναστείλε email' : '📧 Resend email');
                              return (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleResendFromList(bookingCodeValue); }}
                                  disabled={_rs === 'sending'}
                                  className={`mt-2 ml-2 text-xs px-3 py-1 text-white rounded-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed ${_cls}`}
                                  title={lang === 'el' ? 'Ξαναστείλε το check-in email (χωρίς αλλαγή δεδομένων)' : 'Resend check-in email'}
                                >
                                  {_label}
                                </button>
                              );
                            })()}
                            {booking.skipperFirstName && (
                              <div className="text-xs text-gray-600 mt-1">
                                👤 {booking.skipperFirstName} {booking.skipperLastName}
                              </div>
                            )}
                          </div>
                          {bookingCodeValue === currentBookingNumber && (
                            <div className="text-green-600 font-bold text-2xl">✓</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 🔥 TOGGLE BUTTON */}
              <div className="mt-4 pt-4 border-t">
                <button
                  onClick={() => setShowAllBookings(!showAllBookings)}
                  className="w-full px-4 py-3 rounded-lg bg-gray-600 text-white hover:bg-gray-700 font-medium transition-all"
                >
                  {showAllBookings
                    ? `📅 ${t.todayBookings}`
                    : `📋 ${t.showAllBookings} (${allBookings.length} ${t.totalBookings})`
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {showFleet && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{color: brand.black}}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">{lang==='el' ? 'Διαχείριση Στόλου' : 'Manage Fleet'}</h3>
                <button className="text-2xl hover:text-red-500" onClick={()=>setShowFleet(false)}>×</button>
              </div>

              <div className="mb-4">
                <label className="block font-semibold mb-1">
                  {lang==='el' ? 'Νέα κατηγορία' : 'New category'}
                </label>
                <div className="flex gap-2">
                  <input 
                    value={catName} 
                    onChange={e=>setCatName(e.target.value)} 
                    className="flex-1 border rounded p-2" 
                    placeholder={lang==='el' ? 'π.χ. Trimaran' : 'e.g. Trimaran'} 
                  />
                  <button 
                    type="button" 
                    className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600" 
                    onClick={() => { 
                      const name = (catName||'').trim(); 
                      if(!name) return; 
                      if (!fleet[name]) setFleet({...fleet, [name]: []}); 
                      setCatName(''); 
                    }}>
                    {lang==='el' ? 'Προσθήκη' : 'Add'}
                  </button>
                </div>
              </div>

              <div className="max-h-72 overflow-auto border rounded p-3">
                {Object.keys(fleet||{}).length === 0 && (
                  <div className="text-sm text-gray-500">
                    {lang==='el' ? 'Δεν υπάρχουν κατηγορίες.' : 'No categories yet.'}
                  </div>
                )}
                {Object.entries(fleet||{}).map(([c, arr]) => (
                  <div key={c} className="mb-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">{c}</h4>
                      <button 
                        className="text-sm text-red-500 underline" 
                        onClick={() => { 
                          const copy = {...fleet}; 
                          delete copy[c]; 
                          setFleet(copy); 
                          if (form.vesselCategory===c) setForm({...form, vesselCategory:'', vesselName:''}); 
                        }}>
                        {lang==='el' ? 'Διαγραφή κατηγορίας' : 'Delete category'}
                      </button>
                    </div>
                    <ul className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-1">
                      {(arr||[]).map(v => (
                        <li key={v} className="flex items-center justify-between border rounded px-2 py-1 bg-gray-50">
                          <span className="text-sm">{v}</span>
                          <button 
                            className="text-xs text-red-500 underline ml-2" 
                            onClick={() => { 
                              const list = (fleet[c]||[]).filter(x=>x!==v); 
                              setFleet({...fleet, [c]: list}); 
                              if (form.vesselName===v) setForm({...form, vesselName:''}); 
                            }}>
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}